import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    LayoutDashboard, Construction, Users, Package,
    Wallet, Plus, CheckCircle2, Clock, TrendingUp,
    MapPin, Hammer, IndianRupee, ChevronRight, Search,
    Download, HardHat, Calendar, Trash2, Eye, X,
    UserPlus, ArrowLeft, Briefcase, Mail, Lock,
    Camera, Landmark, Receipt, Truck, Layers,
    ArrowUpRight, ArrowDownLeft, Users2, Upload,
    FileSignature, AlertOctagon, Building2,
    Image as ImageIcon2, FolderOpen, Tractor, Wrench,
    RefreshCw, Edit2, KeyRound, Shield, Phone,
    Navigation, Target, Map,
    CheckSquare, AlertTriangle, MoreHorizontal,
    Warehouse, ArrowDownToLine, RotateCcw, Recycle,
    PackageCheck, PackageX, TrendingDown, Send
} from 'lucide-react';
import {
    PieChart, Pie, Cell, ResponsiveContainer,
    Tooltip as RechartsTooltip, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, AreaChart, Area
} from 'recharts';
import { api } from '../api';
import { useServerEvents } from '../useServerEvents';
import NewProjectModal from './NewProjectModal';
import { MATERIAL_CATEGORIES, MATERIALS } from '../materials';
import FinanceDashboard from './FinanceDashboard';
import WarehouseTab from './WarehouseTab';
import GeofenceManager from './GeofenceManager';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Outfit:wght@600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  :root {
    --blue-900: #0f2d6b;
    --blue-800: #1340a0;
    --blue-700: #1a52c9;
    --blue-600: #1d4ed8;
    --blue-500: #3b6fe8;
    --blue-100: #dbeafe;
    --blue-50:  #eff6ff;
    --slate-900: #0f172a;
    --slate-700: #334155;
    --slate-500: #64748b;
    --slate-400: #94a3b8;
    --slate-200: #e2e8f0;
    --slate-100: #f1f5f9;
    --slate-50:  #f8fafc;
    --white:     #ffffff;
    --green-bg:  #dcfce7; --green-fg: #166534;
    --red-bg:    #fee2e2; --red-fg:   #991b1b;
    --amber-bg:  #fef3c7; --amber-fg: #92400e;
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 14px;
    --radius-xl: 18px;
    --shadow-sm: 0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04);
    --shadow-md: 0 4px 6px rgba(15,23,42,0.07), 0 2px 4px rgba(15,23,42,0.05);
    --shadow-lg: 0 10px 24px rgba(15,23,42,0.10), 0 4px 8px rgba(15,23,42,0.06);
    --shadow-xl: 0 24px 48px rgba(15,23,42,0.16), 0 8px 16px rgba(15,23,42,0.08);
  }

  .adm-root {
    display: flex; height: 100%; overflow: hidden;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--white); color: var(--slate-900);
    -webkit-font-smoothing: antialiased;
  }

  /* ── Sidebar ── */
  .adm-sidebar {
    width: 260px; background: var(--white);
    border-right: 1px solid var(--slate-200);
    display: flex; flex-direction: column;
    padding: 0; gap: 0; flex-shrink: 0; z-index: 10; overflow-y: auto;
  }
  .adm-sidebar::-webkit-scrollbar { display: none; }

  .adm-sidebar-top {
    padding: 22px 20px 16px;
    border-bottom: 1px solid var(--slate-100);
    margin-bottom: 8px;
  }

  .adm-logo {
    font-family: 'Outfit', sans-serif;
    font-size: 18px; font-weight: 800; color: var(--blue-600);
    letter-spacing: -0.3px; display: flex; align-items: center; gap: 9px;
    text-decoration: none;
  }
  .adm-logo-icon {
    width: 34px; height: 34px; border-radius: 9px;
    background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-800) 100%);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 2px 8px rgba(29,78,216,0.30);
    flex-shrink: 0;
  }
  .adm-logo-sub {
    font-size: 10px; color: var(--slate-400); font-weight: 500;
    letter-spacing: 0.3px; margin-top: 1px; font-family: 'Plus Jakarta Sans', sans-serif;
  }

  .adm-nav-section {
    padding: 0 12px 4px; font-size: 10px; font-weight: 700;
    color: var(--slate-400); text-transform: uppercase; letter-spacing: 1px;
    margin: 12px 0 4px;
  }

  .adm-nav-item {
    display: flex; align-items: center; gap: 9px;
    padding: 9px 12px; margin: 1px 8px;
    border-radius: var(--radius-md); border: none; background: transparent;
    color: var(--slate-500); cursor: pointer; font-size: 13px;
    transition: all 0.12s ease; width: calc(100% - 16px); text-align: left;
    font-weight: 500; font-family: 'Plus Jakarta Sans', sans-serif;
    letter-spacing: -0.1px;
  }
  .adm-nav-item:hover { background: var(--slate-100); color: var(--slate-900); }
  .adm-nav-item.active {
    background: var(--blue-50); color: var(--blue-600); font-weight: 700;
    box-shadow: inset 3px 0 0 var(--blue-600);
  }
  .adm-nav-item.active svg { color: var(--blue-600); }
  .adm-nav-dot {
    margin-left: auto; width: 6px; height: 6px; border-radius: 50%;
    background: var(--blue-600); flex-shrink: 0;
  }

  .adm-sidebar-footer {
    margin-top: auto; padding: 16px 20px;
    border-top: 1px solid var(--slate-100);
  }
  .adm-user-card {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border-radius: var(--radius-md);
    background: var(--slate-50); border: 1px solid var(--slate-200);
  }
  .adm-avatar {
    width: 36px; height: 36px; border-radius: 10px;
    background: linear-gradient(135deg, var(--blue-600), var(--blue-800));
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 12px; flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(29,78,216,0.25);
  }

  /* ── Main ── */
  .adm-main {
    flex: 1; overflow-y: auto; padding: 32px 36px;
    background: var(--white);
  }
  .adm-main::-webkit-scrollbar { width: 5px; }
  .adm-main::-webkit-scrollbar-thumb { background: var(--slate-200); border-radius: 99px; }
  .adm-main::-webkit-scrollbar-track { background: transparent; }

  /* ── Header ── */
  .adm-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 28px; padding-bottom: 24px;
    border-bottom: 1px solid var(--slate-100);
  }
  .adm-title {
    font-family: 'Outfit', sans-serif;
    font-size: 22px; font-weight: 800; margin: 0; letter-spacing: -0.5px;
    color: var(--slate-900);
  }
  .adm-subtitle { font-size: 12.5px; color: var(--slate-400); margin: 3px 0 0; font-weight: 500; }
  .adm-header-actions { display: flex; gap: 8px; align-items: center; }

  /* ── Cards ── */
  .card {
    background: var(--white); border: 1px solid var(--slate-200);
    border-radius: var(--radius-lg); padding: 22px;
    box-shadow: var(--shadow-sm); transition: box-shadow 0.18s ease, border-color 0.18s ease;
  }
  .card:hover { box-shadow: var(--shadow-md); border-color: #d1d9e8; }

  .card-hdr {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 18px;
  }
  .card-hdr h3 {
    margin: 0; font-size: 14px; font-weight: 700; color: var(--slate-900);
    letter-spacing: -0.2px;
  }

  .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 22px; }

  /* ── Stat cards ── */
  .stat-card {
    background: var(--white); border: 1px solid var(--slate-200);
    border-radius: var(--radius-lg); padding: 20px 22px;
    box-shadow: var(--shadow-sm); transition: all 0.18s ease; cursor: pointer;
    position: relative; overflow: hidden;
  }
  .stat-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: var(--accent, var(--blue-600)); border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  .stat-card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); }
  .stat-icon-wrap {
    width: 40px; height: 40px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
  }
  .stat-label { font-size: 11px; color: var(--slate-400); font-weight: 600; text-transform: uppercase; letter-spacing: 0.7px; }
  .stat-value { font-family: 'Outfit', sans-serif; font-size: 28px; font-weight: 800; margin: 4px 0 2px; letter-spacing: -1px; color: var(--slate-900); line-height: 1; }
  .stat-sub { font-size: 11px; color: var(--slate-400); font-weight: 500; }
  .stat-trend { font-size: 11px; font-weight: 700; display: flex; align-items: center; gap: 3px; float: right; margin-top: -32px; }

  /* ── Badges ── */
  .badge {
    padding: 3px 9px; border-radius: 5px; font-size: 10.5px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.4px;
    display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  }
  .badge::before { content: '●'; font-size: 8px; }
  .badge-on-track,.badge-approved,.badge-resolved,.badge-reviewed,.badge-active { background: var(--green-bg); color: var(--green-fg); }
  .badge-delayed,.badge-rejected,.badge-high,.badge-breakdown,.badge-blacklisted { background: var(--red-bg); color: var(--red-fg); }
  .badge-at-risk,.badge-pending,.badge-medium,.badge-maintenance { background: var(--amber-bg); color: var(--amber-fg); }
  .badge-site,.badge-low,.badge-idle { background: var(--blue-50); color: var(--blue-600); }
  .badge-out-for-delivery { background: #e0e7ff; color: #3730a3; }

  /* ── Progress ── */
  .progress-bg { height: 5px; background: var(--slate-100); border-radius: 99px; overflow: hidden; margin-top: 7px; }
  .progress-fill { height: 100%; background: var(--blue-600); border-radius: 99px; transition: width 0.7s cubic-bezier(.4,0,.2,1); }

  /* ── Table ── */
  .tbl { width: 100%; border-collapse: collapse; }
  .tbl thead { background: var(--slate-50); }
  .tbl th {
    text-align: left; padding: 10px 14px; color: var(--slate-400);
    font-size: 10.5px; border-bottom: 1px solid var(--slate-200);
    text-transform: uppercase; letter-spacing: 0.6px; font-weight: 700;
  }
  .tbl th:first-child { border-radius: var(--radius-sm) 0 0 var(--radius-sm); }
  .tbl th:last-child  { border-radius: 0 var(--radius-sm) var(--radius-sm) 0; }
  .tbl td { padding: 13px 14px; border-bottom: 1px solid var(--slate-100); font-size: 13px; color: var(--slate-700); }
  .tbl tr:last-child td { border-bottom: none; }
  .tbl tr.cr { cursor: pointer; transition: background 0.1s; }
  .tbl tr.cr:hover td { background: var(--blue-50); }
  .tbl-id { font-family: 'Outfit', sans-serif; font-weight: 700; color: var(--blue-600); font-size: 12px; letter-spacing: 0.3px; }

  /* ── Buttons ── */
  .btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: var(--radius-md);
    font-weight: 600; cursor: pointer; transition: all 0.15s;
    font-size: 13px; font-family: 'Plus Jakarta Sans', sans-serif;
    border: none; justify-content: center; letter-spacing: -0.1px; white-space: nowrap;
  }
  .btn-primary {
    background: var(--blue-600); color: #fff;
    box-shadow: 0 1px 2px rgba(29,78,216,0.20);
  }
  .btn-primary:hover { background: var(--blue-800); box-shadow: 0 4px 14px rgba(29,78,216,0.35); transform: translateY(-1px); }
  .btn-ghost {
    background: var(--white); border: 1px solid var(--slate-200);
    color: var(--slate-700); box-shadow: var(--shadow-sm);
  }
  .btn-ghost:hover { background: var(--slate-50); border-color: var(--slate-300); }
  .btn-light { background: var(--blue-50); color: var(--blue-600); border: 1px solid var(--blue-100); }
  .btn-light:hover { background: var(--blue-100); }
  .btn-danger { background: var(--red-bg); color: var(--red-fg); border: 1px solid #fecaca; }
  .btn-danger:hover { background: #fecaca; }
  .btn-success { background: var(--green-bg); color: var(--green-fg); border: 1px solid #bbf7d0; }
  .btn-success:hover { background: #bbf7d0; }
  .btn-warn { background: var(--amber-bg); color: var(--amber-fg); border: 1px solid #fde68a; }
  .btn-warn:hover { background: #fde68a; }

  /* ── Search input ── */
  .search-wrap { position: relative; }
  .search-wrap svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--slate-400); pointer-events: none; }
  .search-input {
    padding: 8px 12px 8px 34px; border-radius: var(--radius-md);
    border: 1px solid var(--slate-200); font-size: 13px; outline: none;
    width: 210px; font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--white); box-shadow: var(--shadow-sm);
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .search-input:focus { border-color: var(--blue-600); box-shadow: 0 0 0 3px var(--blue-50); }
  .search-input::placeholder { color: var(--slate-400); }

  /* ── Refresh btn ── */
  .refresh-btn {
    background: var(--white); border: 1px solid var(--slate-200); padding: 8px 10px;
    border-radius: var(--radius-md); cursor: pointer; display: flex; align-items: center;
    box-shadow: var(--shadow-sm); transition: all 0.15s;
  }
  .refresh-btn:hover { background: var(--slate-50); border-color: var(--slate-300); }

  /* ── Modal ── */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(15,23,42,0.50);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
    backdrop-filter: blur(6px); animation: fadeIn 0.18s;
  }
  .modal-box {
    background: var(--white); width: 100%; max-width: 560px; padding: 0;
    border-radius: var(--radius-xl); box-shadow: var(--shadow-xl);
    max-height: 92vh; overflow-y: auto; animation: slideUp 0.22s cubic-bezier(.4,0,.2,1);
    border: 1px solid var(--slate-200);
  }
  .modal-hdr {
    display: flex; justify-content: space-between; align-items: center;
    padding: 22px 26px 18px; border-bottom: 1px solid var(--slate-100);
  }
  .modal-hdr h2 { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: -0.3px; font-family: 'Outfit', sans-serif; }
  .modal-body { padding: 22px 26px 26px; }
  .modal-close {
    background: var(--slate-100); border: none; cursor: pointer; color: var(--slate-500);
    padding: 6px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
  }
  .modal-close:hover { background: var(--slate-200); color: var(--slate-900); }

  /* ── Form fields ── */
  .fg { margin-bottom: 14px; }
  .fg label { display: block; font-size: 11.5px; font-weight: 700; margin-bottom: 5px; color: var(--slate-700); letter-spacing: 0.1px; }
  .fg input, .fg select, .fg textarea {
    width: 100%; padding: 9px 12px; border: 1px solid var(--slate-200);
    border-radius: var(--radius-md); font-size: 13px; outline: none; box-sizing: border-box;
    transition: border-color 0.15s, box-shadow 0.15s;
    font-family: 'Plus Jakarta Sans', sans-serif; background: var(--white);
    color: var(--slate-900);
  }
  .fg input:focus, .fg select:focus, .fg textarea:focus {
    border-color: var(--blue-600); box-shadow: 0 0 0 3px var(--blue-50);
  }
  .fg textarea { resize: vertical; min-height: 80px; line-height: 1.5; }
  .fg .iw { position: relative; }
  .fg .iw svg { position: absolute; left: 11px; top: 10px; color: var(--slate-400); pointer-events: none; }
  .fg .iw input { padding-left: 34px; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  /* ── Action buttons in tables ── */
  .action-btn {
    background: transparent; border: 1px solid var(--slate-200);
    padding: 5px 9px; border-radius: var(--radius-sm);
    cursor: pointer; font-size: 11px; font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600; display: inline-flex; align-items: center; gap: 4px;
    transition: all 0.12s; white-space: nowrap;
  }
  .action-btn.edit { color: var(--blue-600); border-color: var(--blue-100); }
  .action-btn.edit:hover { background: var(--blue-50); border-color: var(--blue-600); }
  .action-btn.del { color: var(--red-fg); border-color: #fecaca; }
  .action-btn.del:hover { background: var(--red-bg); }
  .action-btn.resolve { color: var(--green-fg); border-color: #86efac; background: var(--green-bg); }
  .action-btn.resolve:hover { background: #bbf7d0; }
  .action-btn.review { color: var(--blue-600); border-color: var(--blue-100); background: var(--blue-50); }
  .action-btn.review:hover { background: var(--blue-100); }
  .action-btn.pw { color: #6d28d9; border-color: #c4b5fd; }
  .action-btn.pw:hover { background: #ede9fe; }

  /* ── Gallery ── */
  .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px,1fr)); gap: 16px; }
  .gallery-card {
    border-radius: var(--radius-md); overflow: hidden;
    border: 1px solid var(--slate-200); background: var(--white);
    transition: all 0.2s; cursor: pointer; box-shadow: var(--shadow-sm);
  }
  .gallery-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); }
  .gallery-img { width: 100%; height: 160px; object-fit: cover; background: var(--slate-100); display: block; transition: transform 0.2s; }
  .gallery-card:hover .gallery-img { transform: scale(1.03); }
  .gallery-info { padding: 14px; }

  /* ── Loading ── */
  .loading-wrap {
    display: flex; align-items: center; justify-content: center;
    padding: 80px; gap: 12px; color: var(--slate-400); font-size: 14px;
    flex-direction: column;
  }

  /* ── Engineer / Finance cards ── */
  .person-card {
    background: var(--white); border: 1px solid var(--slate-200);
    border-radius: var(--radius-lg); padding: 20px;
    box-shadow: var(--shadow-sm); transition: all 0.18s;
    display: flex; gap: 14px; align-items: flex-start;
  }
  .person-card:hover { box-shadow: var(--shadow-md); border-color: #d1d9e8; }

  /* ── Misc utils ── */
  .divider { height: 1px; background: var(--slate-100); margin: 18px 0; }
  .section-label { font-size: 10.5px; font-weight: 700; color: var(--slate-400); text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; }
  .text-link { color: var(--blue-600); background: none; border: none; font-size: 12px; font-weight: 700; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; padding: 0; }
  .text-link:hover { color: var(--blue-800); text-decoration: underline; }

  /* ── Animations ── */
  @keyframes fadeIn  { from{opacity:0}                          to{opacity:1} }
  @keyframes slideUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  .spinning { animation: spin 0.8s linear infinite; }
`;

// ── Inline material form used in indent modal ────────────────
function IndentMaterialForm({ vendors }) {
    const [category, setCategory] = React.useState('');
    const [brand, setBrand] = React.useState('');

    return (
        <>
            <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Material Category *</label>
                <select name="item_category" value={category} onChange={e => { setCategory(e.target.value); setBrand(''); }}
                    style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                    <option value="">-- Select Category --</option>
                    {MATERIAL_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
            </div>
            <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', marginBottom: '4px', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Brand / Manufacturer *</label>
                <select name="item" value={brand} onChange={e => setBrand(e.target.value)} required
                    style={{ width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                    <option value="">-- Select Brand --</option>
                    {(category ? (MATERIALS[category] || []) : []).map(b => <option key={b} value={b}>{b}</option>)}
                </select>
            </div>
            <div className="grid2">
                <div className="fg"><label>Qty</label><input name="qty" type="number" required /></div>
                <div className="fg"><label>UOM</label>
                    <select name="uom">
                        <option>Bags</option><option>Tons</option><option>Trucks</option>
                        <option>m³</option><option>Units</option><option>Litres</option>
                        <option>Metres</option><option>Rolls</option><option>Pieces</option>
                    </select>
                </div>
            </div>
            <div className="fg"><label>Vendor</label>
                <select name="vendor">
                    <option>TBD</option>
                    {vendors.map(v => <option key={v.id}>{v.name}</option>)}
                </select>
            </div>
        </>
    );
}

// Make materials available to vanilla DOM (avoids React re-render on dropdown change)
if (typeof window !== 'undefined') { window.__MR_MATERIALS__ = MATERIALS; }

export default function AdminDashboard({ user }) {
    const [activeTab, setActiveTab] = useState('Control Center');
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedIndentId, setSelectedIndentId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [toast, setToast] = useState(null);
    const [loadingData, setLoadingData] = useState(true);
    // fetchAll still used for manual refresh button
    const [editingTasks, setEditingTasks] = useState([]);

    // Edit state — holds item being edited
    const [editItem, setEditItem] = useState(null);
    const [showNewProject, setShowNewProject] = useState(false);

    const [modals, setModals] = useState({
        project: false, engineer: false, finance: false, indent: false, subcontractor: false,
        stock: false, task: false, vendor: false, issue: false,
        doc: false, equipment: false, labor: false,
        editProject: false, editEngineer: false, editFinance: false,
        editIndent: false, editVendor: false, editIssue: false,
        editEquipment: false, editLabor: false, editDoc: false,
        editDpr: false, editInventory: false,
        changePassword: false,
    });
    const openModal = (k, item = null) => { if (item) setEditItem(item); setModals(m => ({ ...m, [k]: true })); };
    const closeModal = (k) => { setModals(m => ({ ...m, [k]: false })); setEditItem(null); };

    // Data
    const [projects, setProjects] = useState([]);
    const [indents, setIndents] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [financeTeam, setFinanceTeam] = useState([]);
    const [dprs, setDprs] = useState([]);
    const [issues, setIssues] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [laborLog, setLaborLog] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [geofences, setGeofences] = useState([]);
    const [lightbox, setLightbox] = useState(null); // {img, desc, site, uploader, date}
    const [selectedMaterialCat, setSelectedMaterialCat] = useState('');
    const [warehouse, setWarehouse] = useState([]);
    const [warehouseMovements, setWarehouseMovements] = useState([]);

    const showToast = useCallback((msg) => {
        setToast(msg); setTimeout(() => setToast(null), 3000);
    }, []);

    const [liveStatus, setLiveStatus] = useState('connecting'); // 'connecting'|'live'|'offline'

    // Apply a partial update — only update state for keys that arrived
    const applyPartial = useCallback((data) => {
        if (data.projects) setProjects(Array.isArray(data.projects) ? data.projects : []);
        if (data.engineers) setEngineers(Array.isArray(data.engineers) ? data.engineers : []);
        if (data.finance) setFinanceTeam(Array.isArray(data.finance) ? data.finance : []);
        if (data.indents) setIndents(Array.isArray(data.indents) ? data.indents : []);
        if (data.inventory) setInventory(Array.isArray(data.inventory) ? data.inventory : []);
        if (data.dprs) setDprs(Array.isArray(data.dprs) ? data.dprs : []);
        if (data.issues) setIssues(Array.isArray(data.issues) ? data.issues : []);
        if (data.gallery) setGallery(Array.isArray(data.gallery) ? data.gallery : []);
        if (data.vendors) setVendors(Array.isArray(data.vendors) ? data.vendors : []);
        if (data.documents) setDocuments(Array.isArray(data.documents) ? data.documents : []);
        if (data.equipment) setEquipment(Array.isArray(data.equipment) ? data.equipment : []);
        if (data.labor) setLaborLog(Array.isArray(data.labor) ? data.labor : []);
        if (data.warehouse) setWarehouse(Array.isArray(data.warehouse) ? data.warehouse : []);
    }, []);

    // Shared data setter — used by both initial load and background poll
    const applyData = useCallback((proj, eng, fin, ind, inv, dpr, iss, gal, ven, doc, eq, lab, gdn, sub) => {
        setProjects(Array.isArray(proj) ? proj : []);
        setEngineers(Array.isArray(eng) ? eng : []);
        setFinanceTeam(Array.isArray(fin) ? fin : []);
        setIndents(Array.isArray(ind) ? ind : []);
        setInventory(Array.isArray(inv) ? inv : []);
        setDprs(Array.isArray(dpr) ? dpr : []);
        setIssues(Array.isArray(iss) ? iss : []);
        setGallery(Array.isArray(gal) ? gal : []);
        setVendors(Array.isArray(ven) ? ven : []);
        setDocuments(Array.isArray(doc) ? doc : []);
        setEquipment(Array.isArray(eq) ? eq : []);
        setLaborLog(Array.isArray(lab) ? lab : []);
        setWarehouse(Array.isArray(gdn) ? gdn : []);
        setSubcontractors(Array.isArray(sub) ? sub : []);
    }, []);

    const fetchAll = useCallback(async () => {
        setLoadingData(true);
        try {
            const [proj, eng, fin, ind, inv, dpr, iss, gal, ven, doc, eq, lab, gdn, sub, gf] = await Promise.all([
                api('projects'), api('engineers'), api('finance/team'),
                api('indents'), api('inventory'), api('dprs'),
                api('issues'), api('gallery'), api('vendors'),
                api('documents'), api('equipment'), api('labor'), api('warehouse'),
                api('subcontractors'), api('geofence'), api('subcontractors'),
            ]);
            applyData(proj, eng, fin, ind, inv, dpr, iss, gal, ven, doc, eq, lab, gdn, sub);
            setGeofences(Array.isArray(gf) ? gf : []);
        } catch { showToast('⚠ Could not reach server.'); }
        finally { setLoadingData(false); }
    }, [showToast, applyData]);

    // Always load data on mount — SSE is enhancement only, not required
    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Safety timeout — if SSE doesn't init within 6s, load via HTTP anyway
    useEffect(() => {
        const t = setTimeout(() => {
            if (loadingData) { fetchAll(); }
        }, 6000);
        return () => clearTimeout(t);
    }, []); // eslint-disable-line

    // SSE — placed AFTER fetchAll so it can reference it
    useServerEvents({
        onConnected: useCallback(() => setLiveStatus('live'), []),
        onError: useCallback(() => {
            setLiveStatus('offline');
            fetchAll(); // SSE failed — load via HTTP
        }, [fetchAll]),
        onInit: useCallback((data) => {
            applyPartial(data);
            setLoadingData(false);
            setLiveStatus('live');
        }, [applyPartial]),
        onUpdate: useCallback((data) => {
            applyPartial(data);
        }, [applyPartial]),
    });

    const parseLakh = (v) => parseFloat(String(v || 0).replace(/[^0-9.]/g, '')) || 0;
    const totalBudget = useMemo(() => Array.isArray(projects) ? projects.reduce((a, p) => a + parseLakh(p.budget), 0) : 0, [projects]);
    const totalSpent = useMemo(() => Array.isArray(projects) ? projects.reduce((a, p) => a + parseLakh(p.spent), 0) : 0, [projects]);
    const burnPct = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
    const indentStatus = useMemo(() => {
        const c = { Pending: 0, Approved: 0, 'Out for Delivery': 0 };
        indents.forEach(i => { if (c[i.status] !== undefined) c[i.status]++; });
        return [{ name: 'Pending', value: c.Pending, color: '#f59e0b' }, { name: 'Approved', value: c.Approved, color: '#10b981' }, { name: 'Transit', value: c['Out for Delivery'], color: '#3b82f6' }].filter(d => d.value > 0);
    }, [indents]);

    // ── ADD Actions ──────────────────────────────────────────
    const doAddProject = async ({ name, client, location, budget, startDate, endDate, tasks }) => {
        const p = await api('projects', 'POST', { name, client, location, budget, startDate, endDate });
        if (tasks && tasks.length > 0) {
            const overall = Math.round(tasks.reduce((a, t) => a + t.progress, 0) / tasks.length);
            await api(`projects/${p.id}`, 'PUT', { tasks, progress: overall });
        }
        const fresh = await api('projects');
        setProjects(Array.isArray(fresh) ? fresh : []);
        setShowNewProject(false);
        showToast(`Project "${name}" created${tasks?.length ? ` with ${tasks.length} tasks` : ''}.`);
    };
    const doAddEngineer = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('engineers', 'POST', { name: f.get('name'), role: f.get('role'), site: f.get('site'), email: f.get('email'), password: f.get('password') });
            setEngineers(await api('engineers')); closeModal('engineer'); showToast('Engineer onboarded.');
        } catch { showToast('Error.'); }
    };
    const doAddFinance = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('finance/team', 'POST', {
                name: f.get('name'),
                role: f.get('role'),
                email: f.get('email'),
                password: f.get('password'),
                photo: f.get('photo_url') || null,
            });
            setFinanceTeam(await api('finance/team'));
            closeModal('finance');
            showToast('Finance member added. They can now log in with Finance access.');
        } catch (err) {
            showToast(err.message || 'Error adding finance member.');
        }
    };
    const doCreateIndent = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('indents', 'POST', { project: f.get('project'), priority: f.get('priority'), item: f.get('item'), qty: f.get('qty'), uom: f.get('uom'), vendor: f.get('vendor') });
            setIndents(await api('indents')); closeModal('indent'); showToast('Indent raised.');
        } catch { showToast('Error.'); }
    };
    const doUpdateStock = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = new FormData(e.target);
        const siteId = parseInt(f.get('siteId'));
        if (!siteId) { showToast('Please select a site.'); return; }
        if (!f.get('itemName')) { showToast('Please enter item name.'); return; }
        try {
            await api('inventory', 'POST', { siteId, siteName: inventory.find(s => s.siteId === siteId)?.siteName || '', itemName: f.get('itemName'), uom: f.get('uom'), type: f.get('type'), amount: parseInt(f.get('amount') || 0) });
            setInventory(await api('inventory')); closeModal('stock'); showToast('Stock updated.');
        } catch (err) { showToast('Error: ' + (err.message || 'Unknown')); }
    };
    const doAddVendor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('vendors', 'POST', { name: f.get('name'), category: f.get('category'), contact: f.get('contact'), phone: f.get('phone') });
            setVendors(await api('vendors')); closeModal('vendor'); showToast('Vendor added.');
        } catch { showToast('Error.'); }
    };
    const doAddIssue = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('issues', 'POST', { site: f.get('site'), title: f.get('title'), severity: f.get('severity'), assignedTo: f.get('assignedTo') });
            setIssues(await api('issues')); closeModal('issue'); showToast('Issue logged.');
        } catch { showToast('Error.'); }
    };
    const doAddDoc = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('documents', 'POST', { title: f.get('title'), type: f.get('type'), site: f.get('site') });
            setDocuments(await api('documents')); closeModal('doc'); showToast('Document uploaded.');
        } catch { showToast('Error.'); }
    };
    const doAddEquipment = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('equipment', 'POST', { name: f.get('name'), category: f.get('category'), site: f.get('site') });
            setEquipment(await api('equipment')); closeModal('equipment'); showToast('Equipment logged.');
        } catch { showToast('Error.'); }
    };
    const doAddSubcontractor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('subcontractors', 'POST', {
                name: f.get('name'), contact_person: f.get('contact_person'),
                phone: f.get('phone'), email: f.get('email'),
                work_type: f.get('work_type'), location: f.get('location'),
                gst_no: f.get('gst_no'), notes: f.get('notes'),
            });
            setSubcontractors(await api('subcontractors'));
            closeModal('subcontractor'); showToast('Subcontractor added.');
        } catch (err) { showToast(err.message || 'Error.'); }
    };
    const doEditSubcontractor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`subcontractors/${editItem.id}`, 'PUT', {
                name: f.get('name'), contact_person: f.get('contact_person'),
                phone: f.get('phone'), email: f.get('email'),
                work_type: f.get('work_type'), location: f.get('location'),
                gst_no: f.get('gst_no'), status: f.get('status'), notes: f.get('notes'),
            });
            setSubcontractors(await api('subcontractors'));
            closeModal('editSubcontractor'); showToast('Subcontractor updated.');
        } catch { showToast('Error.'); }
    };
    const doDeleteSubcontractor = async (id, name) => {
        if (!window.confirm(`Remove subcontractor ${name}?`)) return;
        try { await api(`subcontractors/${id}`, 'DELETE'); setSubcontractors(p => p.filter(x => x.id !== id)); showToast('Removed.'); }
        catch { showToast('Error.'); }
    };
    const doAddLabor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api('labor', 'POST', { site: f.get('site'), contractor: f.get('contractor'), skilled: f.get('skilled'), unskilled: f.get('unskilled') });
            setLaborLog(await api('labor')); closeModal('labor'); showToast('Attendance logged.');
        } catch { showToast('Error.'); }
    };

    // ── EDIT Actions ─────────────────────────────────────────
    const doEditProject = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`projects/${editItem.id}`, 'PUT', { name: f.get('name'), client: f.get('client'), location: f.get('location'), budget: f.get('budget'), spent: f.get('spent'), status: f.get('status'), delayed_days: f.get('delayed_days'), start_date: f.get('startDate'), end_date: f.get('endDate') });
            setProjects(await api('projects')); closeModal('editProject'); showToast('Project updated.');
        } catch { showToast('Error updating.'); }
    };
    const doEditEngineer = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`engineers/${editItem.id}`, 'PUT', { name: f.get('name'), role: f.get('role'), site: f.get('site'), email: f.get('email'), status: f.get('status'), tasks: f.get('tasks') });
            setEngineers(await api('engineers')); closeModal('editEngineer'); showToast('Engineer updated.');
        } catch { showToast('Error.'); }
    };
    const doChangePassword = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        if (f.get('newPass') !== f.get('confirmPass')) { showToast('Passwords do not match.'); return; }
        try {
            await api(`engineers/${editItem.id}`, 'PUT', { password: f.get('newPass') });
            closeModal('changePassword'); showToast(`Password updated for ${editItem.name}.`);
        } catch { showToast('Error changing password.'); }
    };
    const doEditFinance = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`finance/team/${editItem.id}`, 'PUT', {
                name: f.get('name'),
                role: f.get('role'),
                email: f.get('email'),
                status: f.get('status'),
                password: f.get('password') || undefined,
            });
            setFinanceTeam(await api('finance/team'));
            closeModal('editFinance');
            showToast('Finance member updated.');
        } catch { showToast('Error.'); }
    };
    const doEditIndent = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`indents/${editItem.id}`, 'PUT', { project: f.get('project'), priority: f.get('priority'), status: f.get('status'), item: f.get('item'), qty: f.get('qty'), uom: f.get('uom'), vendor: f.get('vendor'), total_est_cost: f.get('total_est_cost') });
            setIndents(await api('indents')); closeModal('editIndent'); showToast('Indent updated.');
        } catch { showToast('Error.'); }
    };
    const doEditVendor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`vendors/${editItem.id}`, 'PUT', { name: f.get('name'), category: f.get('category'), contact: f.get('contact'), phone: f.get('phone'), status: f.get('status') });
            setVendors(await api('vendors')); closeModal('editVendor'); showToast('Vendor updated.');
        } catch { showToast('Error.'); }
    };
    const doEditIssue = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`issues/${editItem.id}`, 'PUT', { title: f.get('title'), site: f.get('site'), severity: f.get('severity'), status: f.get('status'), assigned_to: f.get('assignedTo') });
            setIssues(await api('issues')); closeModal('editIssue'); showToast('Issue updated.');
        } catch { showToast('Error.'); }
    };
    const doEditEquipment = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`equipment/${editItem.id}`, 'PUT', { name: f.get('name'), category: f.get('category'), site: f.get('site'), status: f.get('status'), hours_logged: f.get('hours_logged') });
            setEquipment(await api('equipment')); closeModal('editEquipment'); showToast('Equipment updated.');
        } catch { showToast('Error.'); }
    };
    const doEditLabor = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`labor/${editItem.id}`, 'PUT', { site: f.get('site'), contractor: f.get('contractor'), skilled: f.get('skilled'), unskilled: f.get('unskilled'), date: f.get('date') });
            setLaborLog(await api('labor')); closeModal('editLabor'); showToast('Labor log updated.');
        } catch { showToast('Error.'); }
    };
    const doEditDoc = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`documents/${editItem.id}`, 'PUT', { title: f.get('title'), type: f.get('type'), site: f.get('site'), status: f.get('status') });
            setDocuments(await api('documents')); closeModal('editDoc'); showToast('Document updated.');
        } catch { showToast('Error.'); }
    };
    const doEditDpr = async (e) => {
        e.preventDefault(); const f = new FormData(e.target);
        try {
            await api(`dprs/${editItem.id}`, 'PUT', { site: f.get('site'), author: f.get('author'), labor_count: f.get('labor_count'), summary: f.get('summary'), status: f.get('status') });
            setDprs(await api('dprs')); closeModal('editDpr'); showToast('DPR updated.');
        } catch { showToast('Error.'); }
    };

    // ── DELETE Actions ───────────────────────────────────────
    const doDeleteProject = async (id) => {
        if (!window.confirm('Remove this site? All tasks will be lost.')) return;
        try { await api(`projects/${id}`, 'DELETE'); setProjects(p => p.filter(x => x.id !== id)); setSelectedProjectId(null); showToast('Site removed.'); }
        catch { showToast('Error.'); }
    };
    const doRemoveEngineer = async (id, name) => {
        if (!window.confirm(`Revoke access for ${name}?`)) return;
        try { await api(`engineers/${id}`, 'DELETE'); setEngineers(p => p.filter(x => x.id !== id)); showToast('Access revoked.'); }
        catch { showToast('Error.'); }
    };
    const doRemoveFinance = async (id, name) => {
        if (!window.confirm(`Remove ${name}? This will also revoke their login access.`)) return;
        try {
            await api(`finance/team/${id}`, 'DELETE');
            setFinanceTeam(p => p.filter(x => x.id !== id));
            showToast('Finance member removed and access revoked.');
        } catch { showToast('Error.'); }
    };
    const doDeleteIndent = async (id) => {
        if (!window.confirm('Delete this indent?')) return;
        try { await api(`indents/${id}`, 'DELETE'); setIndents(p => p.filter(x => x.id !== id)); showToast('Indent deleted.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteVendor = async (id, name) => {
        if (!window.confirm(`Remove vendor ${name}?`)) return;
        try { await api(`vendors/${id}`, 'DELETE'); setVendors(p => p.filter(x => x.id !== id)); showToast('Vendor removed.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteIssue = async (id) => {
        if (!window.confirm('Delete this issue?')) return;
        try { await api(`issues/${id}`, 'DELETE'); setIssues(p => p.filter(x => x.id !== id)); showToast('Issue deleted.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteEquipment = async (id) => {
        if (!window.confirm('Remove this equipment?')) return;
        try { await api(`equipment/${id}`, 'DELETE'); setEquipment(p => p.filter(x => x.id !== id)); showToast('Equipment removed.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteLabor = async (id) => {
        if (!window.confirm('Delete this log entry?')) return;
        try { await api(`labor/${id}`, 'DELETE'); setLaborLog(p => p.filter(x => x.id !== id)); showToast('Log deleted.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteDoc = async (id) => {
        if (!window.confirm('Delete this document?')) return;
        try { await api(`documents/${id}`, 'DELETE'); setDocuments(p => p.filter(x => x.id !== id)); showToast('Document deleted.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteDpr = async (id) => {
        if (!window.confirm('Delete this DPR?')) return;
        try { await api(`dprs/${id}`, 'DELETE'); setDprs(p => p.filter(x => x.id !== id)); showToast('DPR deleted.'); }
        catch { showToast('Error.'); }
    };
    const doDeleteGallery = async (id) => {
        if (!window.confirm('Delete this photo?')) return;
        try { await api(`gallery/${id}`, 'DELETE'); setGallery(p => p.filter(x => x.id !== id)); showToast('Photo deleted.'); }
        catch { showToast('Error.'); }
    };

    // ── Status quick-actions ─────────────────────────────────
    const doIndentStatus = async (id, status) => {
        try { await api(`indents/${id}`, 'PUT', { status }); setIndents(p => p.map(x => x.id === id ? { ...x, status } : x)); showToast(`Indent ${status}.`); }
        catch { showToast('Error.'); }
    };
    const doResolveIssue = async (id) => {
        try { await api(`issues/${id}`, 'PUT', { status: 'Resolved' }); setIssues(p => p.map(x => x.id === id ? { ...x, status: 'Resolved' } : x)); showToast('Issue resolved.'); }
        catch { showToast('Error.'); }
    };
    const doReviewDPR = async (id) => {
        try { await api(`dprs/${id}`, 'PUT', { status: 'Reviewed' }); setDprs(p => p.map(x => x.id === id ? { ...x, status: 'Reviewed' } : x)); showToast('DPR reviewed.'); }
        catch { showToast('Error.'); }
    };
    const doVendorStatus = async (id, status) => {
        try { await api(`vendors/${id}`, 'PUT', { status }); setVendors(p => p.map(x => x.id === id ? { ...x, status } : x)); showToast(`Vendor ${status}.`); }
        catch { showToast('Error.'); }
    };

    // ── Task Manager ─────────────────────────────────────────
    const openTaskModal = (proj) => { setEditingTasks([...(proj.tasks || [])]); openModal('task'); };
    const updTask = (id, k, v) => setEditingTasks(p => p.map(t => t.id === id ? { ...t, [k]: k === 'progress' ? Math.min(100, Math.max(0, parseInt(v) || 0)) : v } : t));
    const delTask = (id) => setEditingTasks(p => p.filter(t => t.id !== id));
    const addTask = () => setEditingTasks(p => [...p, { id: Date.now(), name: 'New Task', progress: 0, status: 'Planned' }]);
    const saveTasks = async () => {
        const overall = editingTasks.length > 0 ? Math.round(editingTasks.reduce((a, t) => a + t.progress, 0) / editingTasks.length) : 0;
        try {
            await api(`projects/${selectedProjectId}`, 'PUT', { progress: overall, tasks: editingTasks });
            setProjects(await api('projects')); closeModal('task'); showToast('Schedule saved.');
        } catch { showToast('Error saving.'); }
    };

    // ── Search ───────────────────────────────────────────────
    const sq = searchQuery.toLowerCase();
    const fProj = projects.filter(p => p.name?.toLowerCase().includes(sq) || p.client?.toLowerCase().includes(sq));
    const fInd = indents.filter(i => i.project?.toLowerCase().includes(sq) || i.item?.toLowerCase().includes(sq));
    const fEng = engineers.filter(e => e.name?.toLowerCase().includes(sq) || e.role?.toLowerCase().includes(sq));
    const fFin = financeTeam.filter(f => f.name?.toLowerCase().includes(sq));
    const fDpr = dprs.filter(d => d.site?.toLowerCase().includes(sq) || d.author?.toLowerCase().includes(sq));
    const fIss = issues.filter(i => i.title?.toLowerCase().includes(sq) || i.site?.toLowerCase().includes(sq));
    const fVen = vendors.filter(v => v.name?.toLowerCase().includes(sq));
    const fGal = gallery.filter(g => g.site?.toLowerCase().includes(sq) || g.desc?.toLowerCase().includes(sq));
    const fDoc = documents.filter(d => d.title?.toLowerCase().includes(sq) || d.site?.toLowerCase().includes(sq));
    const fEq = equipment.filter(e => e.name?.toLowerCase().includes(sq) || e.site?.toLowerCase().includes(sq));
    const fLab = laborLog.filter(l => l.site?.toLowerCase().includes(sq) || l.contractor?.toLowerCase().includes(sq));

    const selectedProject = projects.find(p => p.id === selectedProjectId || p.id === parseInt(selectedProjectId));
    const selectedIndent = indents.find(i => i.id === selectedIndentId);

    const NAV = [
        { id: 'Control Center', icon: LayoutDashboard },
        { id: 'All Projects', icon: Construction },
        { id: 'Daily Reports', icon: FileSignature },
        { id: 'Issues & Snags', icon: AlertOctagon },
        { id: 'Labor & Attendance', icon: HardHat },
        { id: 'Site Inventory', icon: Layers },
        { id: 'Material Indents', icon: Package },
        { id: 'Equipment & Mach', icon: Tractor },
        { id: 'Drawings & Docs', icon: FolderOpen },
        { id: 'Site Gallery', icon: ImageIcon2 },
        { id: 'Warehouse', icon: Warehouse },
        { id: 'Finance Hub', icon: Wallet },
        { id: 'Vendors', icon: Building2 },
        { id: 'Sub-Contractors', icon: Users2 },
        { id: 'Engineers', icon: Users },
        { id: 'Finance Team', icon: Receipt },
        { id: 'Site Geofence Map', icon: Map },
    ];

    const StatCard = ({ title, value, sub, trend, icon: Ic, color, onClick }) => (
        <div className="stat-card" style={{ '--accent': color }} onClick={onClick}>
            <div className="stat-icon-wrap" style={{ background: `${color}18` }}><Ic size={18} color={color} /></div>
            {trend && <div className="stat-trend" style={{ color: trend > 0 ? '#10b981' : '#ef4444' }}>{trend > 0 ? <TrendingUp size={11} /> : <ArrowDownLeft size={11} />}{Math.abs(trend)}%</div>}
            <div className="stat-label">{title}</div>
            <div className="stat-value">{value}</div>
            <div className="stat-sub">{sub}</div>
        </div>
    );

    const GaugeChart = ({ pct }) => {
        const d = [{ value: pct, fill: 'var(--blue-600)' }, { value: 100 - pct, fill: 'var(--slate-200)' }];
        return (
            <div style={{ position: 'relative', width: '100%', height: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={d} cx="50%" cy="70%" startAngle={180} endAngle={0} innerRadius={60} outerRadius={80} paddingAngle={0} dataKey="value" stroke="none">{d.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie></PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', bottom: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '28px', fontWeight: '800' }}>{Math.round(pct)}%</div>
                    <div style={{ fontSize: '11px', color: 'var(--slate-400)' }}>₹{totalSpent.toFixed(1)}L of ₹{totalBudget.toFixed(1)}L</div>
                </div>
            </div>
        );
    };

    const Modal = ({ k, title, children, wide }) => !modals[k] ? null : (
        <div className="modal-overlay">
            <div className="modal-box" style={wide ? { maxWidth: '720px' } : {}}>
                <div className="modal-hdr">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={() => closeModal(k)}><X size={16} /></button>
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    );

    return (
        <>
            <style>{STYLES}</style>
            <div className="adm-root">

                {/* SIDEBAR */}
                <aside className="adm-sidebar">
                    <div className="adm-sidebar-top">
                        <div className="adm-logo">
                            <div className="adm-logo-icon"><Hammer size={16} color="#fff" /></div>
                            <div><div>MR Constructions</div><div className="adm-logo-sub">Admin Control Panel</div></div>
                        </div>
                    </div>
                    <div style={{ padding: '0 0 8px', flex: 1, overflowY: 'auto' }}>
                        <div className="adm-nav-section">Overview</div>
                        {NAV.slice(0, 2).map(n => (
                            <button key={n.id} className={`adm-nav-item ${activeTab === n.id ? 'active' : ''}`} onClick={() => { setActiveTab(n.id); setSelectedProjectId(null); setSelectedIndentId(null); setSearchQuery(''); }}>
                                <n.icon size={14} />{n.id}{activeTab === n.id && <span className="adm-nav-dot" />}
                            </button>
                        ))}
                        <div className="adm-nav-section">Site Operations</div>
                        {NAV.slice(2, 10).map(n => (
                            <button key={n.id} className={`adm-nav-item ${activeTab === n.id ? 'active' : ''}`} onClick={() => { setActiveTab(n.id); setSelectedProjectId(null); setSelectedIndentId(null); setSearchQuery(''); }}>
                                <n.icon size={14} />{n.id}{activeTab === n.id && <span className="adm-nav-dot" />}
                            </button>
                        ))}
                        <div className="adm-nav-section">Finance & People</div>
                        {NAV.slice(10).map(n => (
                            <button key={n.id} className={`adm-nav-item ${activeTab === n.id ? 'active' : ''}`} onClick={() => { setActiveTab(n.id); setSelectedProjectId(null); setSelectedIndentId(null); setSearchQuery(''); }}>
                                <n.icon size={14} />{n.id}{activeTab === n.id && <span className="adm-nav-dot" />}
                            </button>
                        ))}
                    </div>
                    <div className="adm-sidebar-footer">
                        <div className="adm-user-card">
                            <div className="adm-avatar">{user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                            <div>
                                <div style={{ fontSize: '13px', fontWeight: '700', lineHeight: 1.3, color: 'var(--slate-900)' }}>{user?.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--slate-400)', fontWeight: '500' }}>Executive Admin</div>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* MAIN */}
                <main className="adm-main">
                    <div className="adm-header">
                        <div>
                            <h1 className="adm-title">
                                {selectedIndent ? `Indent: ${selectedIndent.id}` : selectedProject ? selectedProject.name : activeTab}
                            </h1>
                            <p className="adm-subtitle">
                                {selectedIndent ? `Procurement • ${selectedIndent.project}` : selectedProject ? `Site View • ${selectedProject.location}` : 'M R Constructions — Administrative Control'}
                            </p>
                        </div>
                        <div className="adm-header-actions">
                            {(selectedProject || selectedIndent) && (
                                <button className="btn btn-ghost" onClick={() => { setSelectedProjectId(null); setSelectedIndentId(null); }}>
                                    <ArrowLeft size={15} /> Back
                                </button>
                            )}
                            {!selectedProject && !selectedIndent && (<>
                                <div className="search-wrap">
                                    <Search size={14} />
                                    <input className="search-input" placeholder="Search…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: '7px', fontSize: '11px', fontWeight: '700' }}>
                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: liveStatus === 'live' ? '#10b981' : liveStatus === 'offline' ? '#ef4444' : '#f59e0b', flexShrink: 0 }} />
                                    <span style={{ color: liveStatus === 'live' ? '#10b981' : liveStatus === 'offline' ? '#ef4444' : '#f59e0b' }}>
                                        {liveStatus === 'live' ? 'Live' : liveStatus === 'offline' ? 'Offline' : 'Connecting'}
                                    </span>
                                </div>
                                <button onClick={fetchAll} className="refresh-btn" title="Refresh data">
                                    <RefreshCw size={14} color="var(--slate-400)" className={loadingData ? 'spinning' : ''} />
                                </button>
                                {activeTab === 'Site Inventory' && <button className="btn btn-primary" onClick={() => openModal('stock')}><Layers size={14} /> Update Stock</button>}
                                {activeTab === 'Material Indents' && <button className="btn btn-primary" onClick={() => openModal('indent')}><Plus size={14} /> Create Indent</button>}
                                {activeTab === 'Engineers' && <button className="btn btn-primary" onClick={() => openModal('engineer')}><UserPlus size={14} /> Add Engineer</button>}
                                {activeTab === 'Issues & Snags' && <button className="btn btn-primary" onClick={() => openModal('issue')}><AlertOctagon size={14} /> Log Issue</button>}
                                {activeTab === 'Vendors' && <button className="btn btn-primary" onClick={() => openModal('vendor')}><Plus size={14} /> Add Vendor</button>}
                                {activeTab === 'Sub-Contractors' && <button className="btn btn-primary" onClick={() => openModal('subcontractor')}><Plus size={14} /> Add Sub-Contractor</button>}
                                {activeTab === 'Drawings & Docs' && <button className="btn btn-primary" onClick={() => openModal('doc')}><Upload size={14} /> Upload Doc</button>}
                                {activeTab === 'Equipment & Mach' && <button className="btn btn-primary" onClick={() => openModal('equipment')}><Plus size={14} /> Log Equipment</button>}
                                {activeTab === 'Labor & Attendance' && <button className="btn btn-primary" onClick={() => openModal('labor')}><Plus size={14} /> Log Attendance</button>}
                                {activeTab === 'Daily Reports' && <button className="btn btn-primary" onClick={() => openModal('editDpr', { id: '', site: '', author: '', labor_count: '', summary: '', status: 'Pending', isNew: true })}><Plus size={14} /> Submit DPR</button>}
                                {activeTab === 'Finance Team' && <button className="btn btn-primary" onClick={() => openModal('finance')}><Plus size={14} /> Add Member</button>}
                                {activeTab === 'Subcontractors' && <button className="btn btn-primary" onClick={() => openModal('subcontractor')}><Plus size={14} /> Add Subcontractor</button>}
                                {(activeTab === 'Control Center' || activeTab === 'All Projects') && <button className="btn btn-primary" onClick={() => setShowNewProject(true)}><Plus size={14} /> New Project</button>}
                            </>)}
                        </div>
                    </div>

                    {loadingData && !projects.length ? (
                        <div className="loading-wrap">
                            <RefreshCw size={28} color="var(--blue-600)" className="spinning" />
                            <div style={{ fontSize: '14px', color: 'var(--slate-400)', fontWeight: '500' }}>Loading dashboard data…</div>
                        </div>
                    ) : (<>

                        {/* ── INDENT DETAIL ── */}
                        {selectedIndent && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px' }}>
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Item Breakdown</h3>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button className="btn btn-light" style={{ fontSize: '12px' }} onClick={() => openModal('editIndent', selectedIndent)}><Edit2 size={13} /> Edit Indent</button>
                                            <button className="btn btn-ghost" style={{ fontSize: '12px' }} onClick={() => {
                                                const indent = selectedIndent;
                                                const rows = [
                                                    'PURCHASE ORDER',
                                                    '================',
                                                    `PO Date: ${new Date().toLocaleDateString('en-IN')}`,
                                                    `Indent ID: ${indent.id}`,
                                                    `Project: ${indent.project}`,
                                                    `Vendor: ${indent.vendor || 'TBD'}`,
                                                    '',
                                                    'ITEM DETAILS',
                                                    '------------',
                                                    `Material: ${indent.item || (indent.items || [])[0]?.item || ''}`,
                                                    `Quantity: ${indent.qty || (indent.items || [])[0]?.qty || ''} ${indent.uom || (indent.items || [])[0]?.uom || ''}`,
                                                    `Est. Cost: ${indent.total_est_cost || indent.totalEstCost || 'TBD'}`,
                                                    `Priority: ${indent.priority}`,
                                                    `Status: ${indent.status}`,
                                                    '',
                                                    'MR Constructions',
                                                    'Authorized Signature: _______________',
                                                ].join('\n');
                                                const blob = new Blob([rows], { type: 'text/plain' });
                                                const a = document.createElement('a');
                                                a.href = URL.createObjectURL(blob);
                                                a.download = `PO_${indent.id}_${indent.project}.txt`;
                                                a.click();
                                                showToast('PO exported.');
                                            }}><Download size={13} /> Export PO</button>
                                        </div>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Material</th><th>Qty</th><th>UOM</th><th>Vendor</th><th>Est. Cost</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {(selectedIndent.items || [{ item: selectedIndent.item, qty: selectedIndent.qty, uom: selectedIndent.uom, vendor: selectedIndent.vendor }]).map((it, i) => (
                                                <tr key={i}>
                                                    <td style={{ fontWeight: '600' }}>{it.item}</td>
                                                    <td style={{ fontWeight: '700' }}>{it.qty}</td>
                                                    <td>{it.uom}</td>
                                                    <td style={{ color: 'var(--slate-400)' }}>{it.vendor}</td>
                                                    <td style={{ fontWeight: '700' }}>{selectedIndent.totalEstCost || selectedIndent.total_est_cost}</td>
                                                    <td><span className={`badge badge-${selectedIndent.status?.toLowerCase().replace(' ', '-')}`}>{selectedIndent.status}</span></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="card">
                                        <h3 style={{ margin: '0 0 14px' }}>Lifecycle</h3>
                                        {['Requested', 'Approved', 'Dispatched'].map((s, i) => (
                                            <div key={s} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                                                <div style={{ background: i === 0 ? '#10b981' : i === 1 && selectedIndent.status !== 'Pending' ? '#10b981' : i === 2 && selectedIndent.status === 'Out for Delivery' ? '#f59e0b' : 'var(--slate-200)', padding: '5px', borderRadius: '50%' }}>
                                                    {i < 2 ? <CheckCircle2 size={12} color="#fff" /> : <Truck size={12} color="#fff" />}
                                                </div>
                                                <span style={{ fontSize: '13px' }}><strong>{s}</strong></span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="card">
                                        <h3 style={{ margin: '0 0 14px' }}>Actions</h3>
                                        {selectedIndent.status === 'Pending' && (
                                            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                                                <button onClick={() => doIndentStatus(selectedIndent.id, 'Approved')} className="btn btn-success" style={{ flex: 1, padding: '11px' }}>✓ Approve</button>
                                                <button onClick={() => doIndentStatus(selectedIndent.id, 'Rejected')} className="btn btn-danger" style={{ flex: 1, padding: '11px' }}>✗ Reject</button>
                                            </div>
                                        )}
                                        {selectedIndent.status === 'Approved' && (
                                            <button onClick={() => doIndentStatus(selectedIndent.id, 'Out for Delivery')} className="btn btn-primary" style={{ width: '100%', padding: '11px', marginBottom: '12px' }}>🚚 Mark Dispatched</button>
                                        )}
                                        <button onClick={() => doDeleteIndent(selectedIndent.id)} className="btn btn-danger" style={{ width: '100%', padding: '9px', fontSize: '12px' }}>Delete Indent</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── PROJECT DETAIL ── */}
                        {!selectedIndent && selectedProject && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '20px' }}>
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Site Progress & Inventory</h3>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button onClick={() => openModal('editProject', selectedProject)} className="btn btn-light" style={{ fontSize: '12px' }}><Edit2 size={13} /> Edit Details</button>
                                            <button onClick={() => openTaskModal(selectedProject)} className="btn btn-ghost" style={{ fontSize: '12px' }}><Calendar size={13} /> Manage Tasks</button>
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '28px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {(selectedProject.tasks || []).map((t, i) => (
                                                <div key={i} style={{ padding: '10px', border: '1px solid #f1f5f9', borderRadius: 'var(--radius-md)' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                        <span style={{ fontSize: '13px', fontWeight: '600' }}>{t.name}</span>
                                                        <span style={{ fontSize: '11px', fontWeight: '700', color: t.status === 'Completed' ? '#10b981' : '#f59e0b' }}>{t.status}</span>
                                                    </div>
                                                    <div className="progress-bg"><div className="progress-fill" style={{ width: `${t.progress}%` }} /></div>
                                                </div>
                                            ))}
                                            {(!selectedProject.tasks || selectedProject.tasks.length === 0) && <div style={{ textAlign: 'center', padding: '24px', color: 'var(--slate-400)', border: '1px dashed #e2e8f0', borderRadius: 'var(--radius-md)', fontSize: '13px' }}>No tasks — click Manage Tasks to add.</div>}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="card" style={{ borderLeft: `5px solid ${(selectedProject.delayed_days || 0) > 0 ? '#ef4444' : '#10b981'}` }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                                            <Clock size={18} color={(selectedProject.delayed_days || 0) > 0 ? '#ef4444' : '#10b981'} />
                                            <h3 style={{ margin: 0, fontSize: '15px' }}>Schedule</h3>
                                        </div>
                                        <div style={{ fontSize: '30px', fontWeight: '800', color: (selectedProject.delayed_days || 0) > 0 ? '#ef4444' : 'var(--slate-900)' }}>{selectedProject.delayed_days || 0} Days</div>
                                        <div style={{ fontSize: '12px', color: 'var(--slate-400)' }}>{(selectedProject.delayed_days || 0) > 0 ? 'Behind Schedule' : 'On Schedule'}</div>
                                    </div>
                                    <div className="card">
                                        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Financials</h3>
                                        {[['Budget', `₹${selectedProject.budget}`, ''], ['Spent', `₹${selectedProject.spent}`, '#ef4444']].map(([l, v, c]) => (
                                            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                <span style={{ color: 'var(--slate-500)', fontSize: '13px' }}>{l}</span>
                                                <span style={{ fontWeight: '700', color: c || 'var(--slate-900)' }}>{v}</span>
                                            </div>
                                        ))}
                                        <div className="progress-bg"><div className="progress-fill" style={{ width: `${(parseFloat(selectedProject.spent) / (parseFloat(selectedProject.budget) || 1)) * 100}%` }} /></div>
                                    </div>
                                    <button onClick={() => doDeleteProject(selectedProject.id)} className="btn btn-danger" style={{ width: '100%', padding: '11px' }}><Trash2 size={14} /> Remove This Site</button>
                                </div>
                            </div>
                        )}

                        {/* ── TAB CONTENT ── */}
                        {!selectedProject && !selectedIndent && (<>

                            {/* CONTROL CENTER */}
                            {activeTab === 'Control Center' && (<>
                                <div className="stat-grid">
                                    <StatCard title="Total Workforce" value={engineers.length + financeTeam.length} sub="Core Team Members" trend={5} icon={Users2} color="#3b82f6" onClick={() => setActiveTab('Engineers')} />
                                    <StatCard title="Open Indents" value={indents.filter(i => i.status === 'Pending').length} sub="Procurement Queue" icon={Package} color="#f59e0b" onClick={() => setActiveTab('Material Indents')} />
                                    <StatCard title="Total Portfolio" value={`₹${totalBudget.toFixed(1)}L`} sub="Group Planned Value" icon={IndianRupee} color="#10b981" onClick={() => setActiveTab('Finance Hub')} />
                                    <StatCard title="Site Risks" value={issues.filter(i => i.status === 'Open').length} sub="Open Issues" icon={AlertOctagon} color="#ef4444" onClick={() => setActiveTab('Issues & Snags')} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '18px', marginBottom: '20px' }}>
                                    <div className="card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                                            <h3 style={{ margin: 0, fontSize: '15px' }}>Project Health Pipeline</h3>
                                            <button onClick={() => setActiveTab('All Projects')} style={{ color: 'var(--blue-600)', background: 'none', border: 'none', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>View All →</button>
                                        </div>
                                        <table className="tbl">
                                            <thead><tr><th>Site & Client</th><th>Issues</th><th>Progress</th><th>Status</th><th></th></tr></thead>
                                            <tbody>
                                                {fProj.slice(0, 5).map(p => {
                                                    const si = issues.filter(i => i.site === p.name && i.status === 'Open').length;
                                                    return (<tr key={p.id} className="cr" onClick={() => setSelectedProjectId(p.id)}>
                                                        <td><div style={{ fontWeight: '600' }}>{p.name}</div><div style={{ fontSize: '11px', color: 'var(--slate-400)' }}>{p.client} · {p.location}</div></td>
                                                        <td style={{ textAlign: 'center' }}><span style={{ fontWeight: 'bold', color: si > 2 ? '#ef4444' : 'var(--slate-400)' }}>{si}</span></td>
                                                        <td style={{ width: '160px' }}><div className="progress-bg"><div className="progress-fill" style={{ width: `${p.progress}%`, background: p.status === 'Delayed' ? '#ef4444' : 'var(--blue-600)' }} /></div></td>
                                                        <td><span className={`badge badge-${p.status?.toLowerCase().replace(' ', '-')}`}>{p.status}</span></td>
                                                        <td><ChevronRight size={14} color="#cbd5e1" /></td>
                                                    </tr>);
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="card">
                                        <h3 style={{ margin: '0 0 8px', fontSize: '15px' }}>Portfolio Budget Burn</h3>
                                        <GaugeChart pct={burnPct} />
                                    </div>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '18px' }}>
                                    <div className="card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('Material Indents')}>
                                        <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Indent Status</h3>
                                        <div style={{ height: '200px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart><Pie data={indentStatus} innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value">{indentStatus.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><RechartsTooltip contentStyle={{ borderRadius: 'var(--radius-md)', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} /></PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
                                            <h3 style={{ margin: 0, fontSize: '15px' }}>Labor Attendance (Weekly)</h3>
                                            <button onClick={() => setActiveTab('Labor & Attendance')} style={{ color: 'var(--blue-600)', background: 'none', border: 'none', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>Detail Roll →</button>
                                        </div>
                                        <div style={{ height: '220px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={laborLog.slice(-7).map((l, i) => ({ day: l.date?.slice(0, 3) || `D${i + 1}`, skilled: parseInt(l.skilled) || 0, unskilled: parseInt(l.unskilled) || 0 }))} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                                                    <defs>
                                                        <linearGradient id="cS" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1d4ed8" stopOpacity={0.25} /><stop offset="95%" stopColor="#1d4ed8" stopOpacity={0} /></linearGradient>
                                                        <linearGradient id="cU" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#94a3b8" stopOpacity={0.25} /><stop offset="95%" stopColor="#94a3b8" stopOpacity={0} /></linearGradient>
                                                    </defs>
                                                    <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                                                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <RechartsTooltip contentStyle={{ borderRadius: 'var(--radius-md)', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                                    <Area type="monotone" dataKey="unskilled" stroke="#94a3b8" fill="url(#cU)" name="Unskilled" />
                                                    <Area type="monotone" dataKey="skilled" stroke="#1d4ed8" fill="url(#cS)" name="Skilled" />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </>)}

                            {/* ALL PROJECTS */}
                            {activeTab === 'All Projects' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Site Master Register</h3>
                                        <button className="btn btn-ghost" style={{ fontSize: '12px' }} onClick={() => {
                                            const rows = [['Site', 'Client', 'Location', 'Budget', 'Progress', 'Status', 'Start', 'End', 'Delay'], ...fProj.map(p => [p.name, p.client, p.location, p.budget, p.progress + '%', p.status, p.start_date || '', p.end_date || '', (p.delayed_days || 0) + 'd'])];
                                            const csv = rows.map(r => r.map(v => '"' + String(v || '').replace(/"/g, '""') + '"').join(',')).join('\n');
                                            const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = 'MRC_Projects.csv'; a.click();
                                            showToast('Exported projects to CSV.');
                                        }}><Download size={13} /> Export</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Site & Client</th><th>Timeline</th><th>Budget</th><th>Progress</th><th>Delay</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fProj.map(p => (
                                                <tr key={p.id} className="cr" onClick={() => setSelectedProjectId(p.id)}>
                                                    <td><div style={{ fontWeight: '700' }}>{p.name}</div><div style={{ fontSize: '11px', color: 'var(--slate-400)' }}>{p.client}</div></td>
                                                    <td style={{ fontSize: '12px' }}>{p.start_date} – {p.end_date}</td>
                                                    <td style={{ fontWeight: '600' }}>₹{p.budget}</td>
                                                    <td style={{ width: '120px' }}><div className="progress-bg"><div className="progress-fill" style={{ width: `${p.progress}%` }} /></div><div style={{ fontSize: '10px', color: 'var(--slate-400)', marginTop: '2px' }}>{p.progress}%</div></td>
                                                    <td><span style={{ fontWeight: 'bold', color: (p.delayed_days || 0) > 0 ? '#ef4444' : '#10b981' }}>{(p.delayed_days || 0) > 0 ? `${p.delayed_days}d` : '–'}</span></td>
                                                    <td><span className={`badge badge-${p.status?.toLowerCase().replace(' ', '-')}`}>{p.status}</span></td>
                                                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="action-btn edit" onClick={() => openModal('editProject', p)}><Edit2 size={11} /> Edit</button>
                                                        <button className="action-btn del" onClick={() => doDeleteProject(p.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* DAILY REPORTS */}
                            {activeTab === 'Daily Reports' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Daily Progress Reports</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('editDpr', { id: '', site: '', author: '', labor_count: '', summary: '', status: 'Pending', isNew: true })}><Plus size={14} /> Submit DPR</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>DPR ID</th><th>Site</th><th>By</th><th>Labor</th><th>Summary</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fDpr.map(d => (
                                                <tr key={d.id} className="cr">
                                                    <td><div style={{ fontWeight: '700', color: 'var(--blue-600)' }}>{d.id}</div><div style={{ fontSize: '10px', color: 'var(--slate-400)' }}>{d.date}</div></td>
                                                    <td style={{ fontWeight: '600' }}>{d.site}</td>
                                                    <td>{d.author}</td>
                                                    <td style={{ fontWeight: '700' }}>{d.labor_count}</td>
                                                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--slate-500)' }}>{d.summary}</td>
                                                    <td><span className={`badge badge-${d.status?.toLowerCase()}`}>{d.status}</span></td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        {d.status === 'Pending' && <button className="action-btn review" onClick={() => doReviewDPR(d.id)}><CheckSquare size={11} /> Review</button>}
                                                        <button className="action-btn edit" onClick={() => openModal('editDpr', d)}><Edit2 size={11} /></button>
                                                        <button className="action-btn del" onClick={() => doDeleteDpr(d.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ISSUES */}
                            {activeTab === 'Issues & Snags' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Quality & Safety Tracking</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('issue')}><AlertOctagon size={14} /> Log Issue</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>ID</th><th>Description</th><th>Site</th><th>Severity</th><th>Assigned</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fIss.map(i => (
                                                <tr key={i.id} className="cr">
                                                    <td style={{ fontWeight: '800' }}>{i.id}</td>
                                                    <td style={{ fontWeight: '600' }}>{i.title}</td>
                                                    <td>{i.site}</td>
                                                    <td><span className={`badge badge-${i.severity?.toLowerCase()}`}>{i.severity}</span></td>
                                                    <td>{i.assigned_to}</td>
                                                    <td style={{ fontSize: '11px', color: 'var(--slate-400)' }}>{i.date}</td>
                                                    <td><span className={`badge badge-${i.status?.toLowerCase()}`}>{i.status}</span></td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        {i.status === 'Open' && <button className="action-btn resolve" onClick={() => doResolveIssue(i.id)}><CheckCircle2 size={11} /> Resolve</button>}
                                                        <button className="action-btn edit" onClick={() => openModal('editIssue', i)}><Edit2 size={11} /></button>
                                                        <button className="action-btn del" onClick={() => doDeleteIssue(i.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* LABOR */}
                            {activeTab === 'Labor & Attendance' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Site Muster Roll</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('labor')}><Plus size={14} /> Log Attendance</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Log ID</th><th>Site</th><th>Contractor</th><th>Skilled</th><th>Unskilled</th><th>Total</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fLab.map(l => (
                                                <tr key={l.id} className="cr">
                                                    <td><div style={{ fontWeight: '700', color: 'var(--blue-600)' }}>{l.id}</div><div style={{ fontSize: '10px', color: 'var(--slate-400)' }}>{l.date}</div></td>
                                                    <td style={{ fontWeight: '600' }}>{l.site}</td>
                                                    <td>{l.contractor}</td>
                                                    <td>{l.skilled}</td><td>{l.unskilled}</td>
                                                    <td style={{ fontWeight: '700' }}>{l.total}</td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="action-btn edit" onClick={() => openModal('editLabor', l)}><Edit2 size={11} /> Edit</button>
                                                        <button className="action-btn del" onClick={() => doDeleteLabor(l.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* SITE INVENTORY */}
                            {activeTab === 'Site Inventory' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {inventory.map(site => (
                                        <div key={site.siteId} className="card">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                                <h3 style={{ margin: 0 }}>{site.siteName}</h3>
                                                <button className="btn btn-light" onClick={() => openModal('stock')}><ArrowUpRight size={13} /> Log Movement</button>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '14px' }}>
                                                {site.items.map(it => (
                                                    <div key={it.id} style={{ padding: '14px', border: '1px solid #f1f5f9', borderRadius: 'var(--radius-md)', background: 'var(--slate-50)' }}>
                                                        <div style={{ fontSize: '10px', color: 'var(--slate-400)', fontWeight: '700', textTransform: 'uppercase' }}>{it.name}</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '6px' }}>
                                                            <div style={{ fontSize: '18px', fontWeight: '800' }}>{it.balance}<small style={{ fontSize: '11px', fontWeight: '400', color: 'var(--slate-400)' }}> {it.unit}</small></div>
                                                            <div style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: it.status === 'Critical' ? '#fee2e2' : it.status === 'Low Stock' ? '#fef3c7' : '#d1fae5', color: it.status === 'Critical' ? '#991b1b' : it.status === 'Low Stock' ? '#92400e' : '#065f46', fontWeight: '700' }}>{it.status}</div>
                                                        </div>
                                                        <div className="progress-bg" style={{ height: '3px', marginTop: '8px' }}><div className="progress-fill" style={{ width: `${(it.consumed / (it.received || 1)) * 100}%`, background: it.status === 'Critical' ? '#ef4444' : 'var(--blue-600)' }} /></div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--slate-400)', marginTop: '5px' }}><span>Used: {it.consumed}</span><span>Left: {Math.round((it.balance / (it.received || 1)) * 100)}%</span></div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* MATERIAL INDENTS */}
                            {activeTab === 'Material Indents' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Procurement Dashboard</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('indent')}><Plus size={14} /> New Indent</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Indent ID</th><th>Site</th><th>Item</th><th>Est. Value</th><th>Priority</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fInd.map(i => (
                                                <tr key={i.id} className="cr" onClick={() => setSelectedIndentId(i.id)}>
                                                    <td style={{ fontWeight: '800', color: 'var(--blue-600)' }}>{i.id}</td>
                                                    <td><div style={{ fontWeight: '600' }}>{i.project}</div><div style={{ fontSize: '10px', color: 'var(--slate-400)' }}>Req: {i.requestedBy || i.requested_by}</div></td>
                                                    <td>{(i.items || [])[0]?.item || i.item}</td>
                                                    <td style={{ fontWeight: '700' }}>{i.totalEstCost || i.total_est_cost}</td>
                                                    <td><span style={{ fontSize: '11px', fontWeight: 'bold', color: i.priority === 'Urgent' ? '#ef4444' : 'var(--slate-900)' }}>{i.priority}</span></td>
                                                    <td><span className={`badge badge-${i.status?.toLowerCase().replace(' ', '-')}`}>{i.status}</span></td>
                                                    <td onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: '5px' }}>
                                                        {i.status === 'Pending' && <button className="action-btn resolve" style={{ background: '#d1fae5', color: '#065f46' }} onClick={() => doIndentStatus(i.id, 'Approved')}>✓ Approve</button>}
                                                        <button className="action-btn edit" onClick={() => openModal('editIndent', i)}><Edit2 size={11} /></button>
                                                        <button className="action-btn del" onClick={() => doDeleteIndent(i.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* EQUIPMENT */}
                            {activeTab === 'Equipment & Mach' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Heavy Machinery & Assets</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('equipment')}><Plus size={14} /> Log Equipment</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Asset ID</th><th>Name</th><th>Category</th><th>Site</th><th>Hours</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fEq.map(eq => (
                                                <tr key={eq.id} className="cr">
                                                    <td style={{ fontWeight: '800', color: 'var(--blue-600)' }}>{eq.id}</td>
                                                    <td style={{ fontWeight: '600' }}>{eq.name}</td>
                                                    <td>{eq.category}</td><td>{eq.site}</td>
                                                    <td style={{ fontWeight: '700' }}>{eq.hours_logged} hrs</td>
                                                    <td><span className={`badge badge-${eq.status?.toLowerCase()}`}>{eq.status}</span></td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="action-btn edit" onClick={() => openModal('editEquipment', eq)}><Edit2 size={11} /> Edit</button>
                                                        <button className="action-btn del" onClick={() => doDeleteEquipment(eq.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* DRAWINGS & DOCS */}
                            {activeTab === 'Drawings & Docs' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Document Control</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('doc')}><Upload size={14} /> Upload Doc</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Doc ID</th><th>Title</th><th>Category</th><th>Site</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fDoc.map(d => (
                                                <tr key={d.id} className="cr">
                                                    <td style={{ fontWeight: '800' }}>{d.id}</td>
                                                    <td style={{ fontWeight: '600' }}>{d.title}</td>
                                                    <td><span style={{ fontSize: '10px', background: 'var(--slate-100)', padding: '3px 7px', borderRadius: '4px' }}>{d.type}</span></td>
                                                    <td>{d.site}</td>
                                                    <td style={{ fontSize: '11px', color: 'var(--slate-400)' }}>{d.date}</td>
                                                    <td><span className={`badge badge-${d.status?.includes('Approved') ? 'approved' : 'pending'}`}>{d.status}</span></td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="action-btn edit" onClick={() => openModal('editDoc', d)}><Edit2 size={11} /> Edit</button>
                                                        <button className="action-btn del" onClick={() => doDeleteDoc(d.id)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* GALLERY */}
                            {activeTab === 'Site Gallery' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Site Visual Feed</h3>
                                        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                                            <Camera size={14} /> Upload Photo
                                            <input type="file" accept="image/*" style={{ display: 'none' }} multiple
                                                onChange={async (e) => {
                                                    const files = Array.from(e.target.files || []);
                                                    if (!files.length) return;
                                                    const site = window.prompt('Which site are these photos for?\n\n' + projects.map(p => p.name).join('\n'), projects[0]?.name || '');
                                                    if (!site) return;
                                                    let ok = 0, fail = 0;
                                                    for (const file of files) {
                                                        const fd = new FormData();
                                                        fd.append('image', file);
                                                        fd.append('site', site);
                                                        fd.append('uploader', user?.name || 'Admin');
                                                        fd.append('desc', file.name.replace(/\.[^.]+$/, ''));
                                                        try {
                                                            const res = await fetch('https://api.themrconstruction.com/gallery', { method: 'POST', body: fd });
                                                            if (res.ok) ok++; else fail++;
                                                        } catch { fail++; }
                                                    }
                                                    const g = await api('gallery');
                                                    setGallery(Array.isArray(g) ? g : []);
                                                    showToast(ok > 0 ? `${ok} photo(s) uploaded successfully!` : `Upload failed (${fail} errors).`);
                                                    e.target.value = '';
                                                }} />
                                        </label>
                                    </div>
                                    <div className="gallery-grid">
                                        {fGal.map(g => (
                                            <div key={g.id} className="gallery-card">
                                                <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox(g)}>
                                                    <img src={g.img} alt={g.desc} className="gallery-img" />
                                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}>
                                                        <Eye size={24} color="#fff" style={{ opacity: 0, transition: 'opacity 0.2s' }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = '1'} />
                                                    </div>
                                                </div>
                                                <div className="gallery-info">
                                                    <h4 style={{ margin: '0 0 3px', fontSize: '13px' }}>{g.site}</h4>
                                                    <p style={{ margin: '0 0 10px', fontSize: '11px', color: 'var(--slate-400)', wordBreak: 'break-all' }}>{g.desc}</p>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: 'var(--slate-400)' }}>
                                                        <span>{g.uploader} · {g.date}</span>
                                                        <button className="action-btn del" style={{ fontSize: '10px', padding: '3px 7px' }} onClick={() => doDeleteGallery(g.id)}><Trash2 size={10} /></button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* WAREHOUSE */}
                            {activeTab === 'Warehouse' && (
                                <WarehouseTab
                                    warehouse={warehouse}
                                    indents={indents}
                                    projects={projects}
                                    user={user}
                                    onRefresh={fetchAll}
                                    showToast={showToast}
                                />
                            )}

                            {/* SUB-CONTRACTORS */}
                            {activeTab === 'Sub-Contractors' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="card">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                            <h3 style={{ margin: 0 }}>Sub-Contractor Register</h3>
                                        </div>
                                        <table className="tbl">
                                            <thead><tr><th>SC ID</th><th>Name</th><th>Trade</th><th>Contact</th><th>Phone</th><th>Site</th><th>Contract Value</th><th>Status</th><th>Actions</th></tr></thead>
                                            <tbody>
                                                {subcontractors.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || s.trade?.toLowerCase().includes(searchQuery.toLowerCase())).map(sc => (
                                                    <tr key={sc.id} className="cr">
                                                        <td style={{ fontWeight: '800', color: 'var(--blue-600)', fontFamily: 'monospace' }}>{sc.id}</td>
                                                        <td style={{ fontWeight: '700' }}>{sc.name}</td>
                                                        <td><span style={{ background: 'var(--blue-50)', color: 'var(--blue-600)', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>{sc.trade}</span></td>
                                                        <td>{sc.contact_person}</td>
                                                        <td style={{ fontSize: '12px' }}>{sc.phone}</td>
                                                        <td style={{ color: 'var(--slate-500)' }}>{sc.site || '—'}</td>
                                                        <td style={{ fontWeight: '700' }}>₹{parseFloat(sc.contract_value || 0).toLocaleString()}</td>
                                                        <td><span className={`badge ${sc.status === 'Active' ? 'badge-active' : sc.status === 'Completed' ? 'badge-resolved' : 'badge-pending'}`}>{sc.status}</span></td>
                                                        <td style={{ display: 'flex', gap: '5px' }}>
                                                            <button className="action-btn edit" onClick={() => openModal('editSubcontractor', sc)}><Edit2 size={11} /> Edit</button>
                                                            <button className="action-btn del" onClick={async () => {
                                                                if (!window.confirm(`Remove ${sc.name}?`)) return;
                                                                try { await api(`subcontractors/${sc.id}`, 'DELETE'); setSubcontractors(p => p.filter(x => x.id !== sc.id)); showToast('Removed.'); } catch { showToast('Error.'); }
                                                            }}><Trash2 size={11} /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {subcontractors.length === 0 && <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: 'var(--slate-400)' }}>No sub-contractors yet. Click Add Sub-Contractor.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* FINANCE HUB — full FinanceDashboard embedded */}
                            {activeTab === 'Finance Hub' && (
                                <div style={{ height: 'calc(100vh - 160px)', overflow: 'hidden', borderRadius: '12px', border: '1px solid var(--slate-200)', boxShadow: 'var(--shadow-sm)' }}>
                                    <FinanceDashboard user={user} />
                                </div>
                            )}

                            {/* VENDORS */}
                            {activeTab === 'Vendors' && (
                                <div className="card">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0 }}>Vendor Directory</h3>
                                        <button className="btn btn-primary" onClick={() => openModal('vendor')}><Plus size={14} /> Add Vendor</button>
                                    </div>
                                    <table className="tbl">
                                        <thead><tr><th>Name</th><th>Category</th><th>Contact</th><th>Phone</th><th>Status</th><th>Actions</th></tr></thead>
                                        <tbody>
                                            {fVen.map(v => (
                                                <tr key={v.id} className="cr">
                                                    <td style={{ fontWeight: '700' }}>{v.name}</td>
                                                    <td>{v.category}</td><td>{v.contact}</td><td>{v.phone}</td>
                                                    <td><span className={`badge ${v.status === 'Active' ? 'badge-on-track' : v.status === 'Blacklisted' ? 'badge-rejected' : 'badge-pending'}`}>{v.status}</span></td>
                                                    <td style={{ display: 'flex', gap: '5px' }}>
                                                        <button className="action-btn edit" onClick={() => openModal('editVendor', v)}><Edit2 size={11} /> Edit</button>
                                                        {v.status !== 'Blacklisted' && <button className="action-btn del" style={{ color: '#92400e', borderColor: '#fde68a', background: '#fef3c7' }} onClick={() => doVendorStatus(v.id, 'Blacklisted')}>Blacklist</button>}
                                                        {v.status === 'Blacklisted' && <button className="action-btn resolve" onClick={() => doVendorStatus(v.id, 'Active')}>Reactivate</button>}
                                                        <button className="action-btn del" onClick={() => doDeleteVendor(v.id, v.name)}><Trash2 size={11} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ENGINEERS */}
                            {activeTab === 'Engineers' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '16px' }}>
                                    {fEng.map(eng => (
                                        <div key={eng.id} className="card" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                            <div style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-lg)', background: 'var(--blue-600)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', fontWeight: '800', flexShrink: 0 }}>
                                                {eng.photo ? <img src={eng.photo} alt={eng.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-lg)' }} /> : eng.name?.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>{eng.name}</h4>
                                                </div>
                                                <div style={{ fontSize: '11px', color: 'var(--slate-400)', marginBottom: '3px' }}>{eng.role}</div>
                                                <div style={{ fontSize: '10px', color: 'var(--blue-600)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '3px' }}><Mail size={9} />{eng.email}</div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '10px' }}><MapPin size={10} color="#1d4ed8" /><span className="badge badge-site" style={{ fontSize: '10px' }}>{eng.site}</span></div>
                                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                    <button className="action-btn edit" onClick={() => openModal('editEngineer', eng)}><Edit2 size={10} /> Edit</button>
                                                    <button className="action-btn pw" onClick={() => openModal('changePassword', eng)}><KeyRound size={10} /> Password</button>
                                                    <button className="action-btn del" onClick={() => doRemoveEngineer(eng.id, eng.name)}><Trash2 size={10} /> Remove</button>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f8fafc' }}>
                                                    <span style={{ fontSize: '10px', color: eng.status === 'On Site' || eng.status === 'Active' ? '#10b981' : '#f59e0b', fontWeight: '700' }}>● {eng.status}</span>
                                                    <span style={{ fontSize: '10px', color: 'var(--slate-400)' }}>{eng.tasks} Tasks</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="card" style={{ border: '2px dashed #e2e8f0', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', boxShadow: 'none', minHeight: '140px' }} onClick={() => openModal('engineer')}>
                                        <div style={{ background: 'var(--slate-100)', padding: '10px', borderRadius: '50%' }}><UserPlus size={20} color="#1d4ed8" /></div>
                                        <span style={{ color: 'var(--slate-400)', fontWeight: '600', fontSize: '13px' }}>Onboard Engineer</span>
                                    </div>
                                </div>
                            )}

                            {/* SUBCONTRACTORS */}
                            {activeTab === 'Subcontractors' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="card">
                                        <div className="card-hdr" style={{ marginBottom: '18px' }}>
                                            <h3 style={{ margin: 0 }}>Subcontractor Directory</h3>
                                            <button className="btn btn-ghost" style={{ fontSize: '12px' }} onClick={() => showToast('Exporting…')}><Download size={13} /> Export</button>
                                        </div>
                                        <table className="tbl">
                                            <thead>
                                                <tr><th>Name</th><th>Contact</th><th>Phone</th><th>Work Type</th><th>Location</th><th>GST No.</th><th>Status</th><th>Actions</th></tr>
                                            </thead>
                                            <tbody>
                                                {subcontractors.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()) || s.work_type?.toLowerCase().includes(searchQuery.toLowerCase())).map(s => (
                                                    <tr key={s.id} className="cr">
                                                        <td style={{ fontWeight: '700' }}>{s.name}</td>
                                                        <td>{s.contact_person}</td>
                                                        <td>{s.phone}</td>
                                                        <td><span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>{s.work_type}</span></td>
                                                        <td style={{ color: 'var(--slate-500)' }}>{s.location}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{s.gst_no || '—'}</td>
                                                        <td><span className={`badge ${s.status === 'Active' ? 'badge-on-track' : 'badge-pending'}`}>{s.status}</span></td>
                                                        <td style={{ display: 'flex', gap: '5px' }}>
                                                            <button className="action-btn edit" onClick={() => openModal('editSubcontractor', s)}><Edit2 size={11} /> Edit</button>
                                                            <button className="action-btn del" onClick={() => doDeleteSubcontractor(s.id, s.name)}><Trash2 size={11} /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {subcontractors.length === 0 && <tr><td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'var(--slate-400)' }}>No subcontractors yet. Click Add Subcontractor to begin.</td></tr>}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* FINANCE TEAM */}
                            {activeTab === 'Finance Team' && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '16px' }}>
                                    {fFin.map(fin => (
                                        <div key={fin.id} className="card" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                            <div style={{ width: '52px', height: '52px', borderRadius: 'var(--radius-lg)', background: '#0891b2', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', fontWeight: '800', flexShrink: 0 }}>
                                                {fin.photo ? <img src={fin.photo} alt={fin.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-lg)' }} /> : fin.name?.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700' }}>{fin.name}</h4>
                                                <div style={{ fontSize: '11px', color: 'var(--slate-400)', marginBottom: '3px' }}>{fin.role}</div>
                                                <div style={{ fontSize: '10px', color: '#0891b2', display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '10px' }}><Mail size={9} />{fin.email}</div>
                                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                                    <button className="action-btn edit" onClick={() => openModal('editFinance', fin)}><Edit2 size={10} /> Edit</button>
                                                    <button className="action-btn pw" onClick={() => openModal('changePassword', fin)}><KeyRound size={10} /> Password</button>
                                                    <button className="action-btn del" onClick={() => doRemoveFinance(fin.id, fin.name)}><Trash2 size={10} /> Remove</button>
                                                </div>
                                                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f8fafc' }}>
                                                    <span style={{ fontSize: '10px', color: fin.status === 'Active' ? '#10b981' : '#f59e0b', fontWeight: '700' }}>● {fin.status}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="card" style={{ border: '2px dashed #e2e8f0', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', boxShadow: 'none', minHeight: '140px' }} onClick={() => openModal('finance')}>
                                        <div style={{ background: '#cffafe', padding: '10px', borderRadius: '50%' }}><Landmark size={20} color="#0891b2" /></div>
                                        <span style={{ color: 'var(--slate-400)', fontWeight: '600', fontSize: '13px' }}>Invite Finance Officer</span>
                                    </div>
                                </div>
                            )}

                            {/* ── GEOFENCE MAP TAB ── */}
                            {activeTab === 'Site Geofence Map' && (
                                <GeofenceManager
                                    projects={projects}
                                    geofences={geofences}
                                    setGeofences={setGeofences}
                                    showToast={showToast}
                                    api={api}
                                />
                            )}

                        </>)}

                    </>)}
                </main>

                {/* ════════════════════════════════════════════════
                ADD MODALS
            ════════════════════════════════════════════════ */}

                {/* Task Manager — Powerplay-style Kanban */}
                {modals.task && selectedProject && (
                    <div className="modal-overlay">
                        <div className="modal-box" style={{ maxWidth: '900px' }}>
                            <div className="modal-hdr">
                                <h2>📋 Schedule — {selectedProject.name}</h2>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '11px', color: 'var(--slate-400)', background: 'var(--slate-100)', padding: '4px 10px', borderRadius: '5px' }}>
                                        {editingTasks.length} tasks · {editingTasks.filter(t => t.status === 'Completed').length} done
                                    </span>
                                    <button className="modal-close" onClick={() => closeModal('task')}><X size={16} /></button>
                                </div>
                            </div>
                            <div className="modal-body">
                                {/* Kanban columns */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                                    {['Planned', 'In Progress', 'Completed', 'Delayed', 'Blocked'].map(col => (
                                        <div key={col} style={{ background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: '10px', padding: '12px', minHeight: '200px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: col === 'Completed' ? '#16a34a' : col === 'Delayed' ? '#dc2626' : col === 'Blocked' ? '#7c3aed' : col === 'In Progress' ? '#d97706' : '#64748b' }}>
                                                    {col}
                                                </span>
                                                <span style={{ background: 'white', border: '1px solid var(--slate-200)', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: '700', color: 'var(--slate-500)' }}>
                                                    {editingTasks.filter(t => t.status === col).length}
                                                </span>
                                            </div>
                                            {editingTasks.filter(t => t.status === col).map(t => (
                                                <div key={t.id} style={{ background: 'white', border: '1px solid var(--slate-200)', borderRadius: '8px', padding: '10px', marginBottom: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                    <input
                                                        value={t.name}
                                                        onChange={e => updTask(t.id, 'name', e.target.value)}
                                                        style={{ width: '100%', border: 'none', outline: 'none', fontSize: '12px', fontWeight: '600', fontFamily: 'Plus Jakarta Sans,sans-serif', marginBottom: '8px', background: 'transparent' }}
                                                    />
                                                    <div style={{ marginBottom: '6px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--slate-400)', marginBottom: '3px' }}>
                                                            <span>Progress</span><span style={{ fontWeight: '700', color: 'var(--blue-600)' }}>{t.progress}%</span>
                                                        </div>
                                                        <input type="range" min="0" max="100" value={t.progress}
                                                            onChange={e => { const v = parseInt(e.target.value); updTask(t.id, 'progress', v); if (v === 100) updTask(t.id, 'status', 'Completed'); else if (v > 0 && t.status === 'Planned') updTask(t.id, 'status', 'In Progress'); }}
                                                            style={{
                                                                width: '100%', height: '4px', borderRadius: '2px', cursor: 'pointer',
                                                                background: `linear-gradient(to right,${t.status === 'Completed' ? '#10b981' : t.status === 'Delayed' ? '#ef4444' : t.status === 'Blocked' ? '#7c3aed' : '#1d4ed8'} ${t.progress}%,#e2e8f0 ${t.progress}%)`
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <select value={t.status} onChange={e => updTask(t.id, 'status', e.target.value)}
                                                            style={{ border: 'none', outline: 'none', fontSize: '10px', fontWeight: '700', fontFamily: 'Plus Jakarta Sans,sans-serif', background: 'transparent', color: t.status === 'Completed' ? '#16a34a' : t.status === 'Delayed' ? '#dc2626' : t.status === 'Blocked' ? '#7c3aed' : t.status === 'In Progress' ? '#d97706' : '#64748b', cursor: 'pointer', padding: '2px 0' }}>
                                                            <option>Planned</option><option>In Progress</option><option>Completed</option><option>Delayed</option><option>Blocked</option>
                                                        </select>
                                                        <button onClick={() => delTask(t.id)} style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '2px' }} onMouseOver={e => e.currentTarget.style.color = '#ef4444'} onMouseOut={e => e.currentTarget.style.color = '#cbd5e1'}>
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            <button onClick={() => { addTask(); setTimeout(() => updTask(editingTasks[editingTasks.length]?.id || Date.now(), 'status', col), 0); }}
                                                style={{ width: '100%', background: 'none', border: '1px dashed var(--slate-200)', borderRadius: '7px', padding: '7px', color: 'var(--slate-400)', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontFamily: 'Plus Jakarta Sans,sans-serif' }}
                                                onClick={addTask}>
                                                <Plus size={11} /> Add task
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                {/* Overall progress bar */}
                                <div style={{ background: 'var(--slate-50)', border: '1px solid var(--slate-200)', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                                        <span style={{ fontWeight: '700' }}>Overall Completion</span>
                                        <span style={{ fontWeight: '800', color: 'var(--blue-600)' }}>
                                            {editingTasks.length > 0 ? Math.round(editingTasks.reduce((a, t) => a + t.progress, 0) / editingTasks.length) : 0}%
                                        </span>
                                    </div>
                                    <div style={{ height: '8px', background: 'var(--slate-200)', borderRadius: '4px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: 'linear-gradient(90deg,#1d4ed8,#3b82f6)', borderRadius: '4px', width: `${editingTasks.length > 0 ? Math.round(editingTasks.reduce((a, t) => a + t.progress, 0) / editingTasks.length) : 0}%`, transition: 'width 0.4s ease' }} />
                                    </div>
                                    <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '11px' }}>
                                        {['Planned', 'In Progress', 'Completed', 'Delayed', 'Blocked'].map(s => (
                                            <span key={s} style={{ color: 'var(--slate-400)' }}>
                                                <span style={{ fontWeight: '800', color: 'var(--slate-700)' }}>{editingTasks.filter(t => t.status === s).length}</span> {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <button className="btn btn-primary" style={{ width: '100%', padding: '12px' }} onClick={saveTasks}>
                                    <CheckCircle2 size={15} /> Save Schedule
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showNewProject && (
                    <NewProjectModal
                        onClose={() => setShowNewProject(false)}
                        onSave={doAddProject}
                        projects={projects}
                    />
                )}

                <Modal k="stock" title="Log Stock Movement">
                    <form onSubmit={doUpdateStock}>
                        <div className="fg"><label>Site</label><select name="siteId" required><option value="">-- Select Site --</option>{inventory.map(s => <option key={s.siteId} value={s.siteId}>{s.siteName}</option>)}</select></div>
                        <div className="fg"><label>Movement Type</label><select name="type"><option value="inward">Inward (Delivered)</option><option value="outward">Outward (Consumed)</option></select></div>
                        <div className="fg"><label>Item Name</label><input name="itemName" placeholder="e.g. Cement" required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Quantity</label><input name="amount" type="number" required /></div>
                            <div className="fg"><label>UOM</label><input name="uom" placeholder="Bags / Tons" required /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Update Inventory</button>
                    </form>
                </Modal>

                <Modal k="indent" title="Raise Material Indent">
                    <form onSubmit={doCreateIndent}>
                        <div className="fg"><label>Project</label><select name="project" required>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: 'var(--radius-md)', background: 'var(--slate-50)', marginBottom: '14px' }}>
                            <div className="fg">
                                <label>Material Category</label>
                                <select id="indentCatSelect" defaultValue="" onChange={e => {
                                    const brandSel = document.getElementById('indentBrandSelect');
                                    if (!brandSel) return;
                                    const cat = e.target.value;
                                    const brands = window.__MR_MATERIALS__?.[cat] || [];
                                    brandSel.innerHTML = '<option value="">-- Select Brand --</option>' + brands.map(b => `<option value="${b}">${b}</option>`).join('');
                                }} style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 'var(--radius-md)', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option value="">-- Select Category --</option>
                                    {MATERIAL_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                </select>
                            </div>
                            <div className="fg">
                                <label>Brand / Manufacturer</label>
                                <select id="indentBrandSelect" name="item" required style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 'var(--radius-md)', fontSize: '13px', outline: 'none', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>
                                    <option value="">-- Select Brand --</option>
                                </select>
                            </div>
                            <div className="grid2">
                                <div className="fg"><label>Qty</label><input name="qty" type="number" required /></div>
                                <div className="fg"><label>UOM</label><select name="uom"><option>Bags</option><option>Tons</option><option>Trucks</option><option>m³</option><option>Units</option><option>Rolls</option><option>Pieces</option><option>Litres</option><option>Metres</option></select></div>
                            </div>
                            <div className="fg"><label>Vendor</label><select name="vendor"><option>TBD</option>{vendors.map(v => <option key={v.id}>{v.name}</option>)}</select></div>
                        </div>
                        <div className="fg"><label>Priority</label><select name="priority"><option>Standard</option><option>High</option><option value="Urgent">Urgent</option></select></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Submit Indent</button>
                    </form>
                </Modal>

                <Modal k="issue" title="Log Site Issue">
                    <form onSubmit={doAddIssue}>
                        <div className="fg"><label>Site</label><select name="site">{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="fg"><label>Issue Title</label><input name="title" placeholder="e.g. Substandard curing on Block B" required /></div>
                        <div className="fg"><label>Severity</label><select name="severity"><option>Low</option><option>Medium</option><option>High</option></select></div>
                        <div className="fg"><label>Assign To</label><select name="assignedTo"><option value="">-- Unassigned --</option>{engineers.map(e => <option key={e.id}>{e.name}</option>)}</select></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Log Issue</button>
                    </form>
                </Modal>

                <Modal k="vendor" title="Add Vendor">
                    <form onSubmit={doAddVendor}>
                        <div className="fg"><label>Vendor Name</label><input name="name" placeholder="Vendor name" required /></div>
                        <div className="fg"><label>Category</label><select name="category"><option>Material Supplier</option><option>Labor Provider</option><option>Equipment Rental</option></select></div>
                        <div className="grid2">
                            <div className="fg"><label>Contact Person</label><input name="contact" required /></div>
                            <div className="fg"><label>Phone</label><input name="phone" required /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Add to Directory</button>
                    </form>
                </Modal>

                <Modal k="doc" title="Upload Document">
                    <form onSubmit={doAddDoc}>
                        <div className="fg"><label>Document Title</label><input name="title" required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Category</label><select name="type"><option>Architectural</option><option>Structural</option><option>MEP</option><option>Contract</option></select></div>
                            <div className="fg"><label>Site</label><select name="site">{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Upload</button>
                    </form>
                </Modal>

                <Modal k="equipment" title="Log Equipment">
                    <form onSubmit={doAddEquipment}>
                        <div className="fg"><label>Equipment Name/Model</label><input name="name" placeholder="Equipment name / model" required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Category</label><select name="category"><option>Earthmoving</option><option>Lifting</option><option>Concreting</option><option>Transport</option></select></div>
                            <div className="fg"><label>Site</label><select name="site">{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Log Equipment</button>
                    </form>
                </Modal>

                <Modal k="labor" title="Log Daily Attendance">
                    <form onSubmit={doAddLabor}>
                        <div className="fg"><label>Site</label><select name="site">{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="fg"><label>Contractor</label><select name="contractor">{vendors.filter(v => v.category === 'Labor Provider').map(v => <option key={v.id}>{v.name}</option>)}<option>Direct Hire</option></select></div>
                        <div className="grid2" style={{ background: 'var(--slate-50)', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid #e2e8f0' }}>
                            <div className="fg"><label>Skilled Workers</label><input name="skilled" type="number" required /></div>
                            <div className="fg"><label>Unskilled Workers</label><input name="unskilled" type="number" required /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '14px' }}>Submit Muster Roll</button>
                    </form>
                </Modal>

                <Modal k="engineer" title="Onboard Engineer">
                    <form onSubmit={doAddEngineer}>
                        <div className="fg"><label>Full Name</label><div className="iw"><Users size={15} /><input name="name" required /></div></div>
                        <div className="fg"><label>Email</label><div className="iw"><Mail size={15} /><input name="email" type="email" required /></div></div>
                        <div className="fg"><label>Password</label><div className="iw"><Lock size={15} /><input name="password" type="password" required /></div></div>
                        <div className="grid2">
                            <div className="fg"><label>Role</label><select name="role"><option>Site Engineer</option><option>Junior Engineer</option><option>Project Manager</option><option>Safety Officer</option></select></div>
                            <div className="fg"><label>Site</label><select name="site"><option>Unassigned</option>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Create Account</button>
                    </form>
                </Modal>

                <Modal k="finance" title="Register Finance Officer">
                    <form onSubmit={doAddFinance}>
                        <div className="fg"><label>Full Name</label><div className="iw"><Users size={15} /><input name="name" required /></div></div>
                        <div className="fg"><label>Email</label><div className="iw"><Mail size={15} /><input name="email" type="email" required /></div></div>
                        <div className="fg"><label>Password</label><div className="iw"><Lock size={15} /><input name="password" type="password" required /></div></div>
                        <div className="fg"><label>Role</label><select name="role"><option>Accounts Manager</option><option>Chief Financial Officer</option><option>Procurement Specialist</option></select></div>
                        <input name="photo_url" placeholder="Photo URL (optional)" style={{ padding: '9px 12px', border: '1px solid var(--slate-200)', borderRadius: 'var(--radius-md)', width: '100%', boxSizing: 'border-box', marginBottom: '14px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans,sans-serif' }} />
                        <button type="submit" className="btn" style={{ width: '100%', padding: '12px', background: '#0891b2', color: '#fff', borderRadius: 'var(--radius-md)', fontWeight: '700', cursor: 'pointer', border: 'none' }}>Create Finance Access</button>
                    </form>
                </Modal>

                {/* ════════════════════════════════════════════════
                EDIT MODALS
            ════════════════════════════════════════════════ */}

                <Modal k="editProject" title="Edit Project Details">
                    {editItem && <form onSubmit={doEditProject}>
                        <div className="fg"><label>Project Name</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="fg"><label>Client</label><input name="client" defaultValue={editItem.client} /></div>
                        <div className="grid2">
                            <div className="fg"><label>Location</label><input name="location" defaultValue={editItem.location} /></div>
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>On Track</option><option>Delayed</option><option>At Risk</option><option>Completed</option></select></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Budget</label><input name="budget" defaultValue={editItem.budget} /></div>
                            <div className="fg"><label>Spent</label><input name="spent" defaultValue={editItem.spent} /></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Start Date</label><input name="startDate" defaultValue={editItem.start_date} /></div>
                            <div className="fg"><label>End Date</label><input name="endDate" defaultValue={editItem.end_date} /></div>
                        </div>
                        <div className="fg"><label>Delayed Days</label><input name="delayed_days" type="number" defaultValue={editItem.delayed_days || 0} /></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editEngineer" title="Edit Engineer Details">
                    {editItem && <form onSubmit={doEditEngineer}>
                        <div className="fg"><label>Full Name</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="fg"><label>Email</label><input name="email" type="email" defaultValue={editItem.email} required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Role</label><select name="role" defaultValue={editItem.role}><option>Site Engineer</option><option>Junior Engineer</option><option>Project Manager</option><option>Safety Officer</option></select></div>
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Active</option><option>On Site</option><option>On Leave</option><option>Inactive</option></select></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}><option>Unassigned</option>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                            <div className="fg"><label>Tasks</label><input name="tasks" type="number" defaultValue={editItem.tasks || 0} /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="changePassword" title="Change Password">
                    {editItem && <form onSubmit={doChangePassword}>
                        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: '18px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            <AlertTriangle size={16} color="#92400e" style={{ flexShrink: 0, marginTop: '1px' }} />
                            <span style={{ fontSize: '13px', color: '#92400e' }}>Changing password for <strong>{editItem.name}</strong>. They will need to use the new password on next login.</span>
                        </div>
                        <div className="fg"><label>New Password</label><div className="iw"><Lock size={15} /><input name="newPass" type="password" placeholder="Min 8 characters" required /></div></div>
                        <div className="fg"><label>Confirm Password</label><div className="iw"><Lock size={15} /><input name="confirmPass" type="password" placeholder="Re-enter password" required /></div></div>
                        <button type="submit" className="btn" style={{ width: '100%', padding: '12px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans,sans-serif' }}><KeyRound size={14} /> Update Password</button>
                    </form>}
                </Modal>

                <Modal k="editFinance" title="Edit Finance Member">
                    {editItem && <form onSubmit={doEditFinance}>
                        <div className="fg"><label>Full Name</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="fg"><label>Email</label><input name="email" type="email" defaultValue={editItem.email} required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Role</label><select name="role" defaultValue={editItem.role}><option>Accounts Manager</option><option>Chief Financial Officer</option><option>Procurement Specialist</option></select></div>
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Active</option><option>Inactive</option></select></div>
                        </div>
                        <div className="fg"><label>New Password <span style={{ fontWeight: 400, color: '#94a3b8' }}>(leave blank to keep existing)</span></label><div className="iw"><Lock size={15} /><input name="password" type="password" placeholder="Leave blank to keep current password" /></div></div>
                        <button type="submit" className="btn" style={{ width: '100%', padding: '12px', background: '#0891b2', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontWeight: '700', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans,sans-serif' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editIndent" title="Edit Material Indent">
                    {editItem && <form onSubmit={doEditIndent}>
                        <div className="fg"><label>Project</label><select name="project" defaultValue={editItem.project}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="fg"><label>Item Description</label><input name="item" defaultValue={editItem.item || editItem.items?.[0]?.item} required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Qty</label><input name="qty" type="number" defaultValue={editItem.qty || editItem.items?.[0]?.qty} /></div>
                            <div className="fg"><label>UOM</label><select name="uom" defaultValue={editItem.uom || editItem.items?.[0]?.uom}><option>Bags</option><option>Tons</option><option>Trucks</option><option>m³</option><option>Units</option></select></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Vendor</label><select name="vendor" defaultValue={editItem.vendor}><option>TBD</option>{vendors.map(v => <option key={v.id}>{v.name}</option>)}</select></div>
                            <div className="fg"><label>Est. Cost</label><input name="total_est_cost" defaultValue={editItem.total_est_cost || editItem.totalEstCost} /></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Priority</label><select name="priority" defaultValue={editItem.priority}><option>Standard</option><option>High</option><option value="Urgent">Urgent</option></select></div>
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Pending</option><option>Approved</option><option>Out for Delivery</option><option>Rejected</option></select></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editVendor" title="Edit Vendor">
                    {editItem && <form onSubmit={doEditVendor}>
                        <div className="fg"><label>Vendor Name</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="fg"><label>Category</label><select name="category" defaultValue={editItem.category}><option>Material Supplier</option><option>Labor Provider</option><option>Equipment Rental</option></select></div>
                        <div className="grid2">
                            <div className="fg"><label>Contact Person</label><input name="contact" defaultValue={editItem.contact} /></div>
                            <div className="fg"><label>Phone</label><input name="phone" defaultValue={editItem.phone} /></div>
                        </div>
                        <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Active</option><option>Inactive</option><option>Blacklisted</option></select></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editIssue" title="Edit Issue">
                    {editItem && <form onSubmit={doEditIssue}>
                        <div className="fg"><label>Issue Title</label><input name="title" defaultValue={editItem.title} required /></div>
                        <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="grid2">
                            <div className="fg"><label>Severity</label><select name="severity" defaultValue={editItem.severity}><option>Low</option><option>Medium</option><option>High</option></select></div>
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Open</option><option>Resolved</option></select></div>
                        </div>
                        <div className="fg"><label>Assigned To</label><select name="assignedTo" defaultValue={editItem.assigned_to}><option value="">-- Unassigned --</option>{engineers.map(e => <option key={e.id}>{e.name}</option>)}</select></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editEquipment" title="Edit Equipment">
                    {editItem && <form onSubmit={doEditEquipment}>
                        <div className="fg"><label>Equipment Name/Model</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Category</label><select name="category" defaultValue={editItem.category}><option>Earthmoving</option><option>Lifting</option><option>Concreting</option><option>Transport</option></select></div>
                            <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        </div>
                        <div className="grid2">
                            <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Active</option><option>Maintenance</option><option>Idle</option><option>Breakdown</option></select></div>
                            <div className="fg"><label>Hours Logged</label><input name="hours_logged" type="number" defaultValue={editItem.hours_logged || 0} /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editLabor" title="Edit Labor Log">
                    {editItem && <form onSubmit={doEditLabor}>
                        <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="fg"><label>Contractor</label><input name="contractor" defaultValue={editItem.contractor} /></div>
                        <div className="fg"><label>Date</label><input name="date" defaultValue={editItem.date} /></div>
                        <div className="grid2" style={{ background: 'var(--slate-50)', padding: '14px', borderRadius: 'var(--radius-md)', border: '1px solid #e2e8f0' }}>
                            <div className="fg"><label>Skilled</label><input name="skilled" type="number" defaultValue={editItem.skilled} /></div>
                            <div className="fg"><label>Unskilled</label><input name="unskilled" type="number" defaultValue={editItem.unskilled} /></div>
                        </div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px', marginTop: '8px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editDoc" title="Edit Document">
                    {editItem && <form onSubmit={doEditDoc}>
                        <div className="fg"><label>Document Title</label><input name="title" defaultValue={editItem.title} required /></div>
                        <div className="grid2">
                            <div className="fg"><label>Category</label><select name="type" defaultValue={editItem.type}><option>Architectural</option><option>Structural</option><option>MEP</option><option>Contract</option></select></div>
                            <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        </div>
                        <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Pending Review</option><option>Approved</option><option>Rejected</option></select></div>
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                    </form>}
                </Modal>

                <Modal k="editDpr" title={editItem?.isNew ? 'Submit DPR' : 'Edit DPR'}>
                    {editItem && <form onSubmit={editItem.isNew ? async (e) => {
                        e.preventDefault(); const f = new FormData(e.target);
                        try {
                            await api('dprs', 'POST', { site: f.get('site'), author: f.get('author'), labor_count: f.get('labor_count'), summary: f.get('summary') });
                            setDprs(await api('dprs')); closeModal('editDpr'); showToast('DPR submitted.');
                        } catch { showToast('Error.'); }
                    } : doEditDpr}>
                        <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site || ''}>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                        <div className="grid2">
                            <div className="fg"><label>Author / Engineer</label><select name="author" defaultValue={editItem.author || ''}><option value="">Select</option>{engineers.map(e => <option key={e.id}>{e.name}</option>)}</select></div>
                            <div className="fg"><label>Labor Count</label><input name="labor_count" type="number" defaultValue={editItem.labor_count || 0} /></div>
                        </div>
                        <div className="fg"><label>Summary</label><textarea name="summary" defaultValue={editItem.summary || ''} placeholder="Brief summary of today's site activities…" rows={4} /></div>
                        {!editItem.isNew && <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Pending</option><option>Reviewed</option></select></div>}
                        <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>{editItem.isNew ? 'Submit DPR' : 'Save Changes'}</button>
                    </form>}
                </Modal>

            </div>

            {/* ADD SUBCONTRACTOR */}
            <Modal k="subcontractor" title="Add Subcontractor">
                <form onSubmit={doAddSubcontractor}>
                    <div className="fg"><label>Company Name *</label><input name="name" placeholder="Subcontractor company name" required /></div>
                    <div className="grid2">
                        <div className="fg"><label>Contact Person</label><input name="contact_person" placeholder="Name" /></div>
                        <div className="fg"><label>Phone *</label><input name="phone" placeholder="+91 XXXXX XXXXX" required /></div>
                    </div>
                    <div className="fg"><label>Email</label><input name="email" type="email" placeholder="email@company.com" /></div>
                    <div className="fg"><label>Work Type / Specialization</label>
                        <select name="work_type">
                            <option>Civil Work</option>
                            <option>Structural Work</option>
                            <option>Masonry</option>
                            <option>Plastering</option>
                            <option>Tiling</option>
                            <option>Painting</option>
                            <option>Plumbing</option>
                            <option>Electrical</option>
                            <option>Carpentry & Woodwork</option>
                            <option>Roofing</option>
                            <option>Waterproofing</option>
                            <option>Flooring</option>
                            <option>False Ceiling</option>
                            <option>Fabrication & Welding</option>
                            <option>Aluminium & Glazing</option>
                            <option>Interior Finishing</option>
                            <option>Landscaping</option>
                            <option>Site Clearing</option>
                            <option>Earthwork & Excavation</option>
                            <option>Equipment Operator</option>
                            <option>Labour Supply</option>
                            <option>General Contractor</option>
                        </select>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Location / Area</label><input name="location" placeholder="City / Area" /></div>
                        <div className="fg"><label>GST Number</label><input name="gst_no" placeholder="22AAAAA0000A1Z5" /></div>
                    </div>
                    <div className="fg"><label>Notes</label><textarea name="notes" placeholder="Any additional notes…" style={{ minHeight: '60px' }} /></div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Add Subcontractor</button>
                </form>
            </Modal>

            {/* EDIT SUBCONTRACTOR */}
            <Modal k="editSubcontractor" title="Edit Subcontractor">
                {editItem && <form onSubmit={doEditSubcontractor}>
                    <div className="fg"><label>Company Name *</label><input name="name" defaultValue={editItem.name} required /></div>
                    <div className="grid2">
                        <div className="fg"><label>Contact Person</label><input name="contact_person" defaultValue={editItem.contact_person} /></div>
                        <div className="fg"><label>Phone</label><input name="phone" defaultValue={editItem.phone} /></div>
                    </div>
                    <div className="fg"><label>Email</label><input name="email" type="email" defaultValue={editItem.email} /></div>
                    <div className="grid2">
                        <div className="fg"><label>Work Type</label>
                            <select name="work_type" defaultValue={editItem.work_type}>
                                <option>Civil Work</option><option>Structural Work</option><option>Masonry</option>
                                <option>Plastering</option><option>Tiling</option><option>Painting</option>
                                <option>Plumbing</option><option>Electrical</option><option>Carpentry & Woodwork</option>
                                <option>Roofing</option><option>Waterproofing</option><option>Flooring</option>
                                <option>False Ceiling</option><option>Fabrication & Welding</option>
                                <option>Aluminium & Glazing</option><option>Interior Finishing</option>
                                <option>Landscaping</option><option>Site Clearing</option>
                                <option>Earthwork & Excavation</option><option>Equipment Operator</option>
                                <option>Labour Supply</option><option>General Contractor</option>
                            </select>
                        </div>
                        <div className="fg"><label>Status</label>
                            <select name="status" defaultValue={editItem.status}>
                                <option>Active</option><option>Inactive</option><option>Blacklisted</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Location</label><input name="location" defaultValue={editItem.location} /></div>
                        <div className="fg"><label>GST Number</label><input name="gst_no" defaultValue={editItem.gst_no} /></div>
                    </div>
                    <div className="fg"><label>Notes</label><textarea name="notes" defaultValue={editItem.notes} style={{ minHeight: '60px' }} /></div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                </form>}
            </Modal>

            {/* Sub-Contractor Modal */}
            <Modal k="subcontractor" title="Add Sub-Contractor">
                <form onSubmit={async (e) => {
                    e.preventDefault(); const f = new FormData(e.target);
                    try {
                        await api('subcontractors', 'POST', { name: f.get('name'), trade: f.get('trade'), contact_person: f.get('contact_person'), phone: f.get('phone'), email: f.get('email'), site: f.get('site'), contract_value: f.get('contract_value'), start_date: f.get('start_date'), end_date: f.get('end_date'), notes: f.get('notes') });
                        setSubcontractors(await api('subcontractors')); closeModal('subcontractor'); showToast('Sub-contractor added.');
                    } catch { showToast('Error.'); }
                }}>
                    <div className="grid2">
                        <div className="fg"><label>Company Name *</label><input name="name" required placeholder="Sub-contractor company name" /></div>
                        <div className="fg"><label>Trade / Specialization</label><select name="trade"><option>Civil Work</option><option>Electrical</option><option>Plumbing</option><option>Painting</option><option>Carpentry</option><option>Tiling</option><option>Waterproofing</option><option>Steel Fabrication</option><option>Masonry</option><option>Landscaping</option><option>HVAC</option><option>Other</option></select></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Contact Person</label><input name="contact_person" placeholder="Name of POC" /></div>
                        <div className="fg"><label>Phone</label><input name="phone" placeholder="+91 XXXXX XXXXX" /></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Email</label><input name="email" type="email" placeholder="contact@company.com" /></div>
                        <div className="fg"><label>Assigned Site</label><select name="site"><option value="">-- Unassigned --</option>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Contract Value (₹)</label><input name="contract_value" type="number" min="0" step="1000" placeholder="0" /></div>
                        <div className="fg"><label>Start Date</label><input name="start_date" placeholder="01 Jan 2025" /></div>
                    </div>
                    <div className="fg"><label>End Date</label><input name="end_date" placeholder="31 Dec 2025" /></div>
                    <div className="fg"><label>Notes</label><textarea name="notes" placeholder="Scope of work, payment terms…" rows={3} /></div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Add Sub-Contractor</button>
                </form>
            </Modal>

            <Modal k="editSubcontractor" title="Edit Sub-Contractor">
                {editItem && <form onSubmit={async (e) => {
                    e.preventDefault(); const f = new FormData(e.target);
                    try {
                        await api(`subcontractors/${editItem.id}`, 'PUT', { name: f.get('name'), trade: f.get('trade'), contact_person: f.get('contact_person'), phone: f.get('phone'), email: f.get('email'), site: f.get('site'), contract_value: f.get('contract_value'), start_date: f.get('start_date'), end_date: f.get('end_date'), status: f.get('status'), notes: f.get('notes') });
                        setSubcontractors(await api('subcontractors')); closeModal('editSubcontractor'); showToast('Updated.');
                    } catch { showToast('Error.'); }
                }}>
                    <div className="grid2">
                        <div className="fg"><label>Company Name *</label><input name="name" defaultValue={editItem.name} required /></div>
                        <div className="fg"><label>Trade</label><select name="trade" defaultValue={editItem.trade}><option>Civil Work</option><option>Electrical</option><option>Plumbing</option><option>Painting</option><option>Carpentry</option><option>Tiling</option><option>Waterproofing</option><option>Steel Fabrication</option><option>Masonry</option><option>Landscaping</option><option>HVAC</option><option>Other</option></select></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Contact Person</label><input name="contact_person" defaultValue={editItem.contact_person} /></div>
                        <div className="fg"><label>Phone</label><input name="phone" defaultValue={editItem.phone} /></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Email</label><input name="email" defaultValue={editItem.email} /></div>
                        <div className="fg"><label>Site</label><select name="site" defaultValue={editItem.site}><option value="">-- Unassigned --</option>{projects.map(p => <option key={p.id}>{p.name}</option>)}</select></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Contract Value (₹)</label><input name="contract_value" type="number" defaultValue={editItem.contract_value} /></div>
                        <div className="fg"><label>Status</label><select name="status" defaultValue={editItem.status}><option>Active</option><option>Completed</option><option>Suspended</option><option>Terminated</option></select></div>
                    </div>
                    <div className="grid2">
                        <div className="fg"><label>Start Date</label><input name="start_date" defaultValue={editItem.start_date} /></div>
                        <div className="fg"><label>End Date</label><input name="end_date" defaultValue={editItem.end_date} /></div>
                    </div>
                    <div className="fg"><label>Notes</label><textarea name="notes" defaultValue={editItem.notes} rows={3} /></div>
                    <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '12px' }}>Save Changes</button>
                </form>}
            </Modal>

            {/* LIGHTBOX — full size photo viewer */}
            {lightbox && (
                <div onClick={() => setLightbox(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9998, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', backdropFilter: 'blur(4px)' }}>
                    <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img src={lightbox.img} alt={lightbox.desc}
                            style={{ maxWidth: '90vw', maxHeight: '78vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }} />
                        <div style={{ marginTop: '14px', textAlign: 'center', color: '#fff', maxWidth: '600px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '4px' }}>{lightbox.site}</div>
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px', wordBreak: 'break-all' }}>{lightbox.desc}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>{lightbox.uploader} · {lightbox.date}</div>
                        </div>
                    </div>
                    <button onClick={() => setLightbox(null)}
                        style={{ position: 'fixed', top: '16px', right: '20px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '20px', zIndex: 9999 }}>
                        ✕
                    </button>
                    <div style={{ position: 'fixed', bottom: '20px', color: '#64748b', fontSize: '11px' }}>
                        Click anywhere to close
                    </div>
                </div>
            )}

            {/* TOAST */}
            {toast && (
                <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: 'var(--slate-900)', color: '#fff', padding: '13px 20px', borderRadius: '12px', boxShadow: '0 8px 28px rgba(15,23,42,0.30)', display: 'flex', alignItems: 'center', gap: '10px', zIndex: 9999, fontWeight: '600', fontSize: '13px', borderLeft: '4px solid #10b981', animation: 'slideUp 0.22s cubic-bezier(.4,0,.2,1)', fontFamily: 'Plus Jakarta Sans,sans-serif', letterSpacing: '-0.1px' }}>
                    <CheckCircle2 size={16} color="#10b981" />{toast}
                </div>
            )}
        </>
    );
}